
import { SubjectType } from '../types';
import { HISTORY_CHRONOLOGY } from './history/chronology';

// ÖSYM'nin yaklaşık soru dağılımına göre konuların ağırlığı ve zorluk seviyesi
// Her konu için hangi zorluk havuzundan çekileceği belirtilir

export interface TopicWeight {
    topic: string;
    topics?: string[];
    count: number;
}

// Her dersin toplam soru sayısı (KPSS gerçek dağılıma uygun)
export const TARGET_QUESTION_COUNTS: Record<SubjectType, number> = {
    geography: 18,
    history: 27,
    citizenship: 15,
    turkish: 30,
    math: 30
};

// Her sınavda dinamik zorluk dağılımı (%30 Kolay, %40 Orta, %30 Zor)
export type DifficultyType = 'Kolay' | 'Orta' | 'Zor';

/**
 * Verilen toplam soru sayısına göre rastgele zorluk dağılımı üretir.
 * Her çağrıda farklı bir dağılım çıkar.
 * Yaklaşık: %30 Kolay, %40 Orta, %30 Zor
 */
export function generateDifficultyDistribution(totalCount: number): DifficultyType[] {
    const easyCount = Math.round(totalCount * 0.30);
    const hardCount = Math.round(totalCount * 0.30);
    const mediumCount = totalCount - easyCount - hardCount;
    
    const distribution: DifficultyType[] = [
        ...Array(easyCount).fill('Kolay'),
        ...Array(mediumCount).fill('Orta'),
        ...Array(hardCount).fill('Zor')
    ];
    
    // Shuffle — her seferinde farklı sıra
    for (let i = distribution.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [distribution[i], distribution[j]] = [distribution[j], distribution[i]];
    }
    
    return distribution;
}

/**
 * Blueprint toplamını hedef soru sayısına eşitler (en büyük kalan yöntemi; kesir payı büyük konunun ek soru alma şansı yüksek,
 * pay rastgele dağıtılır → her sınavda dağılım biraz farklı çıkar, tıpkı ÖSYM gibi). Sıfıra düşen konu listeden çıkar.
 * Toplam zaten hedefse blueprint aynen döner. Örn. Matematik: 45'lik konu ağırlıkları 30 soruya ölçeklenir.
 */
export function scaleBlueprint(bp: TopicWeight[], target: number, rand: () => number = Math.random): TopicWeight[] {
    const sum = bp.reduce((s, b) => s + b.count, 0);
    if (sum === target || sum <= 0 || target <= 0) return bp;
    const exact = bp.map(b => (b.count * target) / sum);
    const counts = exact.map(Math.floor);
    const frac = exact.map((e, i) => e - counts[i]);
    let left = target - counts.reduce((a, b) => a + b, 0);
    while (left > 0) {
        const total = frac.reduce((a, b) => a + b, 0);
        if (total <= 0) { counts[Math.floor(rand() * counts.length)]++; left--; continue; }
        let r = rand() * total;
        let pick = frac.findIndex(f => f > 0 && (r -= f) < 0);
        if (pick < 0) pick = frac.findIndex(f => f > 0);
        counts[pick]++;
        frac[pick] = 0;
        left--;
    }
    return bp.map((b, i) => ({ ...b, count: counts[i] })).filter(b => b.count > 0);
}

export const EXAM_BLUEPRINTS: Record<SubjectType, TopicWeight[]> = {
    // ═══════════════════════════════════════════════
    // COĞRAFYA: 9 KONU — 18 SORU (ÖSYM 2023 Dağılımı)
    // Ulaşım+Ticaret+Turizm birleşik: repository'de tek topic olarak tutulduğu için
    // ayrı blueprint'te eşleşme sağlanamıyor. ÖSYM'de ayrı ama havuzda birleşik.
    // Sıralama: yüksek soru sayılı konular önce (kota kesilme riski azaltmak için)
    // ═══════════════════════════════════════════════
    geography: [
        { topic: "Türkiye'nin Fiziki Özellikleri", count: 4 },
        { topic: "Türkiye'nin Coğrafi Konumu", count: 2 },
        { topic: "Türkiye'nin İklimi ve Bitki Örtüsü", count: 2 },
        { topic: "Türkiye'de Nüfus ve Yerleşme", count: 2 },
        { topic: "Türkiye'de Madenler ve Enerji Kaynakları", count: 2 },
        { topic: "Türkiye'de Ulaşım, Ticaret ve Turizm", count: 3 },
        { topic: "Türkiye'de Tarım", count: 1 },
        { topic: "Türkiye'de Hayvancılık", count: 1 },
        { topic: "Türkiye'de Sanayi", count: 1 },
    ],

    // ═══════════════════════════════════════════════
    // TARİH: 21 ÜNİTE — 27 SORU
    // chronology.ts label'ları ile birebir senkron
    // ═══════════════════════════════════════════════
    history: [
        { topic: "İlk ve Orta Çağlarda Türk Dünyası", count: 3 },                                           // u1
        { topic: "Türklerin İslamiyet'i Kabulü ve İlk Türk-İslam Devletleri", count: 2 },                    // u2
        { topic: "Yerleşme ve Devletleşme Sürecinde Selçuklu Türkiyesi", count: 1 },                         // u3
        { topic: "Osmanlı Devleti Kültür ve Medeniyeti", count: 3 },                                         // u4
        // Osmanlı Siyasi Tarihi (Toplam 4 Soru — Kuruluştan 20. Yüzyıla dengeli dağılım)
        { topic: "Beylikten Devlete Osmanlı Devleti", topics: ["Beylikten Devlete Osmanlı Devleti", "Dünya Gücü Osmanlı"], count: 1 }, // u5/u6
        { topic: "Değişen Dünya Dengeleri Karşısında Osmanlı Devleti (XVII. Yüzyıl)", topics: ["Değişen Dünya Dengeleri Karşısında Osmanlı Devleti (XVII. Yüzyıl)", "Değişen Dünya Dengeleri Karşısında Osmanlı Siyaseti (XVIII. Yüzyıl)"], count: 1 }, // u7/u8
        { topic: "En Uzun Yüzyıl (XIX. Yüzyılda Osmanlı Devleti)", topics: ["En Uzun Yüzyıl (XIX. Yüzyılda Osmanlı Devleti)", "XIX. Yüzyıl Osmanlı Devleti Islahatları"], count: 1 }, // u9/u10
        { topic: "XX. Yüzyıl Başlarında Osmanlı Devleti ve Dünya", count: 1 },                                // u11
        // Kurtuluş Savaşı ve İnkılap Tarihi (Toplam 11 Soru)
        { topic: "Mustafa Kemal Atatürk'ün Hayatı", count: 1 },                                              // u15
        { topic: "Millî Mücadele Hazırlık Dönemi", count: 1 },                                               // u12
        { topic: "I. TBMM'nin Açılması, İç Ayaklanmalar ve Sevr Barış Antlaşması", count: 1 },                // u13
        { topic: "Millî Mücadele Muharebeler Dönemi", count: 2 },                                            // u14
        { topic: "Atatürk Dönemi İç Politika Gelişmeleri", count: 1 },                                       // u16
        { topic: "Atatürk İnkılapları", count: 2 },                                                          // u18
        { topic: "Atatürk İlkeleri", count: 1 },                                                             // u17
        { topic: "Cumhuriyet Dönemi Kültür ve Medeniyeti", count: 1 },                                       // u19
        { topic: "Atatürk Dönemi Dış Politikası", count: 1 },                                                // u20
        // Çağdaş Türk ve Dünya Tarihi (Toplam 3 Soru)
        { topic: "Çağdaş Türk ve Dünya Tarihi", count: 3 },                                                  // u21
    ],


    // ═══════════════════════════════════════════════
    // VATANDAŞLIK: 8 SAF ÜNİTE (9 SORU) + GÜNCEL BİLGİLER (6 SORU) = 15 SORU
    // Uluslararası Kuruluşlar, 6 Güncel Bilgiler içindedir
    // ═══════════════════════════════════════════════
    citizenship: [
        { topic: "Hukukun Temel Kavramları", count: 2 },
        { topic: "Türk Anayasa Tarihi", count: 1 },
        { topic: "1982 Anayasası ve Temel Esaslar", count: 1 },
        { topic: "Temel Hak ve Hürriyetler", count: 1 },
        { topic: "Yasama", count: 1 },
        { topic: "Yürütme", count: 1 },
        { topic: "Yargı", count: 1 },
        { topic: "İdare Hukuku", count: 1 },
        { topic: "Güncel Bilgiler", count: 6 }
    ],

    // ═══════════════════════════════════════════════
    // TÜRKÇE: 10 ÜNİTE — 30 SORU (RAG & Çıkmış Sorular Senkronlu)
    // ═══════════════════════════════════════════════
    turkish: [
        { topic: "Sözcükte Anlam", count: 4 },
        { topic: "Cümlede Anlam", count: 3 },
        { topic: "Paragraf", count: 9 },
        { topic: "Biçim Bilgisi", count: 2 },
        { topic: "Ses Bilgisi", count: 1 },
        { topic: "Yazım Kuralları", count: 1 },
        { topic: "Noktalama İşaretleri", count: 1 },
        { topic: "Sözcük Türleri", count: 3 },
        { topic: "Cümlenin Ögeleri - Cümle Türleri", count: 2 },
        { topic: "Sözel Mantık", count: 4 }
    ],

    // ═══════════════════════════════════════════════
    // MATEMATİK: 38 KONU — 30 SORU (Blueprint & Çıkmış Sorular Kitabı Senkronlu)
    // ═══════════════════════════════════════════════
    math: [
        { topic: "Temel Kavramlar", count: 2 },
        { topic: "Tek ve Çift Sayılar", count: 1 },
        { topic: "Asal Sayılar", count: 1 },
        { topic: "Ardışık Sayılar", count: 1 },
        { topic: "Faktöriyel", count: 1 },
        { topic: "Basamak Analizi", count: 1 },
        { topic: "Bölme - Bölünebilme", count: 1 },
        { topic: "Ebob - Ekok", count: 1 },
        { topic: "Rasyonel Sayılar", count: 1 },
        { topic: "Ondalıklı Sayılar", count: 1 },
        { topic: "Pozitif - Negatif Sayılar ve Basit Eşitsizlikler", count: 1 },
        { topic: "Mutlak Değer", count: 1 },
        { topic: "Üslü Sayılar", count: 1 },
        { topic: "Köklü Sayılar", count: 1 },
        { topic: "Çarpanlara Ayırma", count: 1 },
        { topic: "Denklem Çözme", count: 1 },
        { topic: "Oran Orantı", count: 1 },
        { topic: "Sayı - Kesir Problemleri", count: 3 },
        { topic: "Yaş Problemleri", count: 1 },
        { topic: "Yüzde Problemleri", count: 1 },
        { topic: "Kar - Zarar Problemleri", count: 1 },
        { topic: "Karışım Problemi", count: 1 },
        { topic: "Periyodik Problemler", count: 1 },
        { topic: "Hareket Problemleri", count: 1 },
        { topic: "İşçi Problemleri", count: 1 },
        { topic: "Tablo Grafik Problemi", count: 1 },
        { topic: "Kümeler", count: 1 },
        { topic: "Fonksiyonlar", count: 1 },
        { topic: "İşlem", count: 1 },
        { topic: "Permütasyon-Kombinasyon-Olasılık", count: 1 },
        { topic: "Özel Sayılar", count: 1 },
        { topic: "Sayısal Mantık", count: 2 },
        { topic: "Doğruda ve Üçgende Açılar", count: 1 },
        { topic: "Dik, İkizkenar ve Eşkenar Üçgen", count: 1 },
        { topic: "Açıortay ve Kenarortay", count: 1 },
        { topic: "Üçgende Alan ve Benzerlik", count: 1 },
        { topic: "Çokgenler", count: 1 },
        { topic: "Dörtgenler", count: 1 },
        { topic: "Çember - Daire", count: 1 },
        { topic: "Katı Cisimler", count: 1 },
        { topic: "Analitik Geometri", count: 1 }
    ]
};

// ═══════════════════════════════════════════════════════════════════════
// 🗂️ COĞRAFYA 3-KATMANLI TAKSONOMİ
// Katman 1: Fiziki / Beşeri / Ekonomik (üst gruplandırma — UI filtre/tab)
// Katman 2: Blueprint ana üniteleri (EXAM_BLUEPRINTS.geography ile birebir)
// Katman 3: KPSS'te çıkan alt başlıklar (detaylı izleme & AI hedefleme)
// ═══════════════════════════════════════════════════════════════════════

/** Katman 1 → Katman 2: Hangi blueprint konusu hangi üst kategoriye ait */
export type GeographyCategoryKey = 'Fiziki Coğrafya' | 'Beşeri Coğrafya' | 'Ekonomik Coğrafya';

export const GEOGRAPHY_CATEGORIES: Record<GeographyCategoryKey, string[]> = {
    'Fiziki Coğrafya': [
        "Türkiye'nin Coğrafi Konumu",
        "Türkiye'nin İklimi ve Bitki Örtüsü",
        "Türkiye'nin Fiziki Özellikleri",
    ],
    'Beşeri Coğrafya': [
        "Türkiye'de Nüfus ve Yerleşme",
    ],
    'Ekonomik Coğrafya': [
        "Türkiye'de Tarım",
        "Türkiye'de Hayvancılık",
        "Türkiye'de Madenler ve Enerji Kaynakları",
        "Türkiye'de Sanayi",
        "Türkiye'de Ulaşım, Ticaret ve Turizm",
    ],
};

/** Katman 2 → Katman 3: Her blueprint konusunun KPSS alt başlıkları */
export const GEOGRAPHY_SUBTOPICS: Record<string, string[]> = {
    // ═══════════════════════════════════════════════════════════
    // 🌍 FİZİKİ COĞRAFYA
    // ═══════════════════════════════════════════════════════════

    "Türkiye'nin Coğrafi Konumu": [
        "Koordinat Sistemi (Paralel ve Meridyenler)",
        "Enlem-Boylam Özellikleri ve Etkileri",
        "Matematik (Mutlak) Konum ve Sonuçları",
        "Göreceli Konum ve Sonuçları",
        "Yerel Saat ve Saat Farkı Hesaplamaları",
        "Özel Tarihler ve Gündüz Süreleri",
        "Dünyanın Şekli ve Hareketleri (Mevsimler)",
        "Güneş Işınlarının Geliş Açısı ve Bakı Etkisi",
        "Harita Bilgisi (Ölçek, İzohips, Projeksiyon)",
        "Türkiye'nin Komşuları, Sınırları ve Sınır Kapıları",
        "Uç Noktalar ve Gerçek/İz Düşüm Alan",
    ],

    "Türkiye'nin İklimi ve Bitki Örtüsü": [
        "İklim Elemanları (Sıcaklık, Yağış, Nem, Basınç)",
        "İklimi Etkileyen Faktörler",
        "Akdeniz İklimi ve Bitki Örtüsü (Maki)",
        "Karadeniz İklimi ve Bitki Örtüsü (Orman)",
        "Karasal İklim ve Bitki Örtüsü (Step/Bozkır)",
        "Sert Karasal İklim",
        "Marmara (Geçiş) İklimi",
        "Rüzgarlar ve Basınç Merkezleri",
        "Yağış Dağılışı ve Türleri",
        "Sıcaklık Dağılışı ve Enlem İlişkisi",
    ],

    "Türkiye'nin Fiziki Özellikleri": [
        "Jeolojik Yapı, Jeolojik Zamanlar ve Tektonik",
        "Deprem ve Fay Hatları",
        "Dağlar (Kıvrım, Kırık, Volkanik, Masif)",
        "Ovalar (Delta, Tektonik, Karstik, Volkanik, Dağ Eteği)",
        "Platolar (Lav, Karstik, Aşınım, Tabaka Düzlüğü)",
        "Akarsular, Akarsu Havzaları ve Kapalı Havzalar",
        "Göller (Tektonik, Volkanik, Karstik, Set, Lagün)",
        "Kıyı Tipleri (Tombolo, Falez, Körfez, Yarımada, Boğaz)",
        "Toprak Tipleri (Zonal, Azonal, İntrazonal, Terra Rossa)",
        "İç Kuvvetler (Volkanizma, Orojenez, Epirojenez)",
        "Dış Kuvvetler (Erozyon, Heyelan, Çığ, Rüzgar, Dalga)",
        "Karstik Şekiller ve Mağaralar",
        "Buzul ve Periglasyal Şekiller",
        "Doğal Afetler (Deprem, Sel, Heyelan, Orman Yangını)",
        "Geçitler, Dağ Geçitleri ve Tüneller",
        "Milli Parklar, Ramsar Alanları ve Şelaleler",
        "Su Varlığı ve Kaynak Suları",
        "Bölgesel Kalkınma Projeleri (GAP, DAP, DOKAP, KOP)",
    ],

    // ═══════════════════════════════════════════════════════════
    // 👥 BEŞERİ COĞRAFYA
    // ═══════════════════════════════════════════════════════════

    "Türkiye'de Nüfus ve Yerleşme": [
        "Nüfus Artışı, Nüfus Artış Hızı ve Sayımlar",
        "Nüfus Yoğunluğu ve Dağılışı",
        "Nüfus Politikaları ve Nüfus Piramitleri",
        "İç Göçler (Kırdan Kente, Mevsimlik)",
        "Dış Göçler (Beyin Göçü, İşçi Göçü)",
        "Kır Yerleşmeleri ve Konut Tipleri",
        "Kent Yerleşmeleri ve Şehirleşme",
        "Fonksiyonel Şehir Sınıflandırması",
    ],

    // ═══════════════════════════════════════════════════════════
    // 💰 EKONOMİK COĞRAFYA
    // ═══════════════════════════════════════════════════════════

    "Türkiye'de Tarım": [
        "Tarımı Etkileyen Faktörler ve Tarım Yöntemleri",
        "Tahıllar (Buğday, Arpa, Mısır, Çavdar, Pirinç)",
        "Sanayi/Endüstri Bitkileri (Pamuk, Tütün, Şeker Pancarı, Çay)",
        "Baklagiller (Nohut, Mercimek, Fasulye)",
        "Yağ Bitkileri (Zeytin, Ayçiçeği, Fındık, Susam)",
        "Meyvecilik (Turunçgil, Üzüm, Elma, İncir, Kayısı, Kivi)",
        "Yumrulu Bitkiler (Patates, Soğan)",
        "Sulama ve Su Kaynakları Yönetimi",
    ],

    "Türkiye'de Hayvancılık": [
        "Büyükbaş Hayvancılık (Sığır)",
        "Küçükbaş Hayvancılık (Koyun, Keçi)",
        "Kümes Hayvancılığı (Tavukçuluk)",
        "Arıcılık",
        "İpek Böcekçiliği",
        "Balıkçılık ve Su Ürünleri",
        "Ormancılık ve Orman Varlığı",
    ],

    "Türkiye'de Madenler ve Enerji Kaynakları": [
        "Metalik Madenler (Demir, Bakır, Krom, Boksit)",
        "Endüstriyel Hammaddeler (Bor, Mermer, Tuz)",
        "Taş Kömürü (Zonguldak Havzası)",
        "Linyit Yatakları ve Kullanım Alanları",
        "Petrol ve Doğal Gaz Kaynakları",
        "Hidroelektrik Enerji (HES) ve Barajlar",
        "Jeotermal Enerji Kaynakları",
        "Güneş Enerjisi (GES) Potansiyeli",
        "Rüzgar Enerjisi (RES) Potansiyeli",
        "Nükleer Enerji Santralleri (Akkuyu)",
        "Enerji ve Boru Hatları (BTC, TANAP, TürkAkım)",
    ],

    "Türkiye'de Sanayi": [
        "Sanayileşme Süreci ve Sanayi Kuruluş Faktörleri",
        "Besin/Gıda Sanayi",
        "Dokuma/Tekstil Sanayi",
        "Demir-Çelik Sanayi",
        "Otomotiv Sanayi",
        "Kimya Endüstrisi",
        "Makine ve Maden Sanayi",
        "Organize Sanayi Bölgeleri (OSB)",
    ],

    "Türkiye'de Ulaşım, Ticaret ve Turizm": [
        "Karayolu Ulaşımı ve Otoyollar",
        "Demiryolu Ulaşımı (YHT, Marmaray)",
        "Havayolu Ulaşımı ve Havalimanları",
        "Denizyolu Ulaşımı ve Limanlar",
        "İç Ticaret ve Pazar Yapısı",
        "Dış Ticaret (İhracat/İthalat Ürünleri)",
        "Serbest Ticaret Bölgeleri",
        "Turizm Türleri (Kültür, Sağlık, Kış, Yayla, Eko)",
        "UNESCO Dünya Mirası Alanları",
        "Cittaslow (Sakin Şehir) Ağı",
    ],
};

/** Yardımcı: Bir blueprint konusunun hangi üst kategoriye ait olduğunu döndürür */
export function getGeographyCategory(blueprintTopic: string): GeographyCategoryKey | null {
    for (const [category, topics] of Object.entries(GEOGRAPHY_CATEGORIES)) {
        if (topics.includes(blueprintTopic)) return category as GeographyCategoryKey;
    }
    return null;
}

/** Yardımcı: Kategoriye göre emoji ikonu döndürür */
export function getCategoryIcon(category: GeographyCategoryKey): string {
    switch (category) {
        case 'Fiziki Coğrafya': return '🌍';
        case 'Beşeri Coğrafya': return '👥';
        case 'Ekonomik Coğrafya': return '💰';
    }
}

/**
 * 🗺️ db.ts alt_kategori → Blueprint ünite eşlemesi
 * Sidebar'daki Katmanlar panelinde alt_kategorileri ünite başlıkları altında gruplamak için kullanılır.
 * Her alt_kategori, EXAM_BLUEPRINTS.geography'deki bir üniteye eşlenir.
 */
export const ALT_KATEGORI_TO_UNITE: Record<string, string> = {
    // ═══════════════════════════════════════
    // FİZİKİ → Türkiye'nin Fiziki Özellikleri
    // ═══════════════════════════════════════
    "Masif Arazi": "Türkiye'nin Fiziki Özellikleri",
    "Kıvrım Dağ": "Türkiye'nin Fiziki Özellikleri",
    "Kırık Dağ": "Türkiye'nin Fiziki Özellikleri",
    "Volkanik Dağ": "Türkiye'nin Fiziki Özellikleri",
    "Delta Ovası": "Türkiye'nin Fiziki Özellikleri",
    "Tektonik Ova": "Türkiye'nin Fiziki Özellikleri",
    "Volkanik Ova": "Türkiye'nin Fiziki Özellikleri",
    "Karstik Ova": "Türkiye'nin Fiziki Özellikleri",
    "Karstik Ova (Polye)": "Türkiye'nin Fiziki Özellikleri",
    "Dağ Eteği Ovası": "Türkiye'nin Fiziki Özellikleri",
    "Plato (Volkanik)": "Türkiye'nin Fiziki Özellikleri",
    "Plato (Karstik)": "Türkiye'nin Fiziki Özellikleri",
    "Plato (Tabaka Düz.)": "Türkiye'nin Fiziki Özellikleri",
    "Plato (Aşınım)": "Türkiye'nin Fiziki Özellikleri",
    "Aşınım Platosu": "Türkiye'nin Fiziki Özellikleri",
    "Yatay Plato": "Türkiye'nin Fiziki Özellikleri",
    "Akarsu": "Türkiye'nin Fiziki Özellikleri",
    "Göl (Tektonik)": "Türkiye'nin Fiziki Özellikleri",
    "Göl (Lav Set)": "Türkiye'nin Fiziki Özellikleri",
    "Göl (Volkanik Set)": "Türkiye'nin Fiziki Özellikleri",
    "Göl (Karstik)": "Türkiye'nin Fiziki Özellikleri",
    "Göl (Kıyı Set)": "Türkiye'nin Fiziki Özellikleri",
    "Göl (Heyelan Set)": "Türkiye'nin Fiziki Özellikleri",
    "Göl (Alüvyal Set)": "Türkiye'nin Fiziki Özellikleri",
    "Göl (Alüvyon Set)": "Türkiye'nin Fiziki Özellikleri",
    "Göl (Volkanik Krater)": "Türkiye'nin Fiziki Özellikleri",
    "Göl (Traverten Set)": "Türkiye'nin Fiziki Özellikleri",
    "Göl (Buzul)": "Türkiye'nin Fiziki Özellikleri",
    "Körfez": "Türkiye'nin Fiziki Özellikleri",
    "Boğaz": "Türkiye'nin Fiziki Özellikleri",
    "Yarımada": "Türkiye'nin Fiziki Özellikleri",
    "Kıyı Şekli": "Türkiye'nin Fiziki Özellikleri",
    "Geçit": "Türkiye'nin Fiziki Özellikleri",
    "Geçit / Tünel": "Türkiye'nin Fiziki Özellikleri",
    "Mağara": "Türkiye'nin Fiziki Özellikleri",
    "Milli Park": "Türkiye'nin Fiziki Özellikleri",
    "Ramsar Alanı": "Türkiye'nin Fiziki Özellikleri",
    "Doğal Oluşum": "Türkiye'nin Fiziki Özellikleri",
    "Şelale": "Türkiye'nin Fiziki Özellikleri",

    // ═══════════════════════════════════════
    // BEŞERİ → Ulaşım, Ticaret ve Turizm
    // (db.ts "Beşeri" kategorisindeki alt_kategoriler aslında KPSS'te ekonomik konulardır)
    // ═══════════════════════════════════════
    "Sınır Kapısı": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    "Kara Sınırı": "Türkiye'nin Coğrafi Konumu",
    "Nüfus (Yoğun Alan)": "Türkiye'de Nüfus ve Yerleşme",
    "Nüfus (Seyrek Alan)": "Türkiye'de Nüfus ve Yerleşme",
    "Nüfus (Göç Veren)": "Türkiye'de Nüfus ve Yerleşme",
    "Nüfus (Göç Alan)": "Türkiye'de Nüfus ve Yerleşme",
    "Balıkçılık": "Türkiye'de Hayvancılık",
    "Tarım (Çavdar)": "Türkiye'de Tarım",
    "Tarım (Yulaf)": "Türkiye'de Tarım",
    "Tarım (Nohut)": "Türkiye'de Tarım",
    "Tarım (Fasulye)": "Türkiye'de Tarım",
    "Tarım (Patates)": "Türkiye'de Tarım",
    "Tarım (Soğan)": "Türkiye'de Tarım",
    "Tarım (Sarımsak)": "Türkiye'de Tarım",
    "Liman": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    "Ulaşım (Demiryolu)": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    "Ulaşım (Havalimanı)": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    "Ulaşım (Kruvaziyer)": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    "Ulaşım (Köprü)": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    "Ulaşım (Liman)": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    "Ulaşım (RO-RO)": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    "Ulaşım (Tünel)": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    "Ulaşım (YHT)": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    "Turizm (Kültür)": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    "Turizm (Doğal)": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    "Turizm (Karma)": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    "Turizm (İnanç)": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    "UNESCO": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    "Cittaslow": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    "Kalkınma Projesi": "Türkiye'de Ulaşım, Ticaret ve Turizm",

    // ═══════════════════════════════════════
    // EKONOMİK → Tarım
    // ═══════════════════════════════════════
    "Tarım (Buğday)": "Türkiye'de Tarım",
    "Tarım (Pirinç)": "Türkiye'de Tarım",
    "Tarım (Pamuk)": "Türkiye'de Tarım",
    "Tarım (Tütün)": "Türkiye'de Tarım",
    "Tarım (Şeker Pancarı)": "Türkiye'de Tarım",
    "Tarım (Çay)": "Türkiye'de Tarım",
    "Tarım (Fındık)": "Türkiye'de Tarım",
    "Tarım (Zeytin)": "Türkiye'de Tarım",
    "Tarım (Ayçiçeği)": "Türkiye'de Tarım",
    "Tarım (Susam)": "Türkiye'de Tarım",
    "Tarım (Mercimek)": "Türkiye'de Tarım",
    "Tarım (Turunçgil)": "Türkiye'de Tarım",
    "Tarım (Üzüm)": "Türkiye'de Tarım",
    "Tarım (İncir)": "Türkiye'de Tarım",
    "Tarım (Kayısı)": "Türkiye'de Tarım",
    "Tarım (Kivi)": "Türkiye'de Tarım",
    "Tarım (Muz)": "Türkiye'de Tarım",
    "Tarım (Meyve)": "Türkiye'de Tarım",
    "Tarım (Aspir)": "Türkiye'de Tarım",
    "Tarım (Antepfıstığı)": "Türkiye'de Tarım",
    "Tarım (Yer Fıstığı)": "Türkiye'de Tarım",
    "Tarım (Keten-Kenevir)": "Türkiye'de Tarım",
    "Tarım (Gül)": "Türkiye'de Tarım",
    "Tarım (Sanayi)": "Türkiye'de Tarım",
    "Tarım (Mikroklima)": "Türkiye'de Tarım",

    // ═══════════════════════════════════════
    // EKONOMİK → Hayvancılık
    // ═══════════════════════════════════════
    "Tarım (Hayvancılık)": "Türkiye'de Hayvancılık",
    "Tarım (Bal)": "Türkiye'de Hayvancılık",
    "Tarım (İpek)": "Türkiye'de Hayvancılık",

    // ═══════════════════════════════════════
    // EKONOMİK → Madenler ve Enerji
    // ═══════════════════════════════════════
    "Maden (Demir)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Bakır)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Krom)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Bor)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Boksit)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Altın)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Alüminyum)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Manganez)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Nikel)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Wolfram)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Molibden)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Kurşun-Çinko)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Cıva)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Asfaltit)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Mermer)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Tuz)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Zımpara)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Perlit)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Pomza)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Feldspat)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Barit)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Bentonit)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Dolomit)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Fosfat)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Kükürt)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Süs Taşı)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Toryum)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Trona)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Uranyum)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Linyit)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Taş Kömürü)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Maden (Petrol)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Baraj (HES)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Baraj (Sulama)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Enerji (Linyit)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Enerji (Taşkömürü)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Enerji (Petrol)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Enerji (Doğal Gaz)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Enerji (Doğalgaz)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Enerji (Jeotermal)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Enerji (Güneş)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Enerji (Rüzgar)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Enerji (RES)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Enerji (Nükleer)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Enerji (Termik)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Enerji (Boru Hattı)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Enerji (LNG)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Enerji (Biyokütle)": "Türkiye'de Madenler ve Enerji Kaynakları",
    "Enerji (Biyogaz)": "Türkiye'de Madenler ve Enerji Kaynakları",

    // ═══════════════════════════════════════
    // EKONOMİK → Sanayi
    // ═══════════════════════════════════════
    "Sanayi (Gıda)": "Türkiye'de Sanayi",
    "Sanayi (Tekstil)": "Türkiye'de Sanayi",
    "Sanayi (Demir-Çelik)": "Türkiye'de Sanayi",
    "Sanayi (Otomotiv)": "Türkiye'de Sanayi",
    "Sanayi (Kimya)": "Türkiye'de Sanayi",
    "Sanayi (Gübre)": "Türkiye'de Sanayi",
    "Sanayi (Petrokimya)": "Türkiye'de Sanayi",
    "Sanayi (Cam)": "Türkiye'de Sanayi",
    "Sanayi (Seramik)": "Türkiye'de Sanayi",
    "Sanayi (Kağıt)": "Türkiye'de Sanayi",
    "Sanayi (Maden)": "Türkiye'de Sanayi",
    "Sanayi (Metalurji)": "Türkiye'de Sanayi",
    "Sanayi (Deri)": "Türkiye'de Sanayi",
    "Sanayi (Halı)": "Türkiye'de Sanayi",
    "Sanayi (Mobilya)": "Türkiye'de Sanayi",
    "Sanayi (Orman Ürünleri)": "Türkiye'de Sanayi",
    "Sanayi (Ulaşım)": "Türkiye'de Sanayi",
    "Sanayi (Savunma)": "Türkiye'de Sanayi",
    "Sanayi (Sigara)": "Türkiye'de Sanayi",
    "Sanayi (Sigara/Tütün)": "Türkiye'de Sanayi",
    "Sanayi (Şeker)": "Türkiye'de Sanayi",
    "Sanayi (OSB)": "Türkiye'de Sanayi",
};

/** Yardımcı: Bir alt_kategori'nin ünite başlığını döndürür, bulunamazsa null */
export function getUniteForAltKategori(altKategori: string): string | null {
    return ALT_KATEGORI_TO_UNITE[altKategori] || null;
}

/**
 * 📖 Engin Hoca kitap sırasına göre alt_kategori sıralama dizisi
 * Sidebar'daki katmanlar bu sıraya göre gösterilir.
 * Fiziki → Beşeri → Ekonomik akışını takip eder.
 */
export const GEOGRAPHY_ALT_KATEGORI_SIRASI: string[] = [
    // ═══════════════════════════════════════
    // 🌍 FİZİKİ ÖZELLİKLER (Engin Hoca s.10-76)
    // ═══════════════════════════════════════
    // Jeolojik Yapı & Yer Şekilleri
    "Masif Arazi",              // I. Jeolojik Zaman Arazileri (s.12)
    "Kıvrım Dağ",              // Kıvrım Dağları (s.14)
    "Kırık Dağ",               // Kırık Dağları - Horst (s.15)
    "Volkanik Dağ",            // Volkanik Dağlar (s.16)
    // Platolar
    "Plato (Karstik)",         // Karstik Platolar (s.17)
    "Plato (Volkanik)",        // Volkanik Platolar (s.17)
    "Plato (Aşınım)",          // Aşınım Platoları (s.18)
    "Plato (Tabaka Düz.)",     // Tabaka Düzlüğü Platoları (s.18)
    "Aşınım Platosu",          // Aşınım Platoları (s.18)
    "Yatay Plato",             // Yatay Plato (s.18)
    // Ovalar
    "Karstik Ova (Polye)",     // Karstik (Polye) Ovaları (s.19)
    "Karstik Ova",             // Karstik Ovalar (s.19)
    "Delta Ovası",             // Delta Ovaları (s.19)
    "Tektonik Ova",            // Tektonik Ovalar (s.20)
    "Volkanik Ova",            // Volkanik Ovalar (s.20)
    "Dağ Eteği Ovası",         // Dağ Eteği (Birikinti) Ovaları (s.20)
    // Karstik Şekiller
    "Mağara",                  // Başlıca Mağaralar (s.25)
    // Kıyılar
    "Kıyı Şekli",             // Falez, Tombolo, Kıyı Tipleri (s.26-29)
    "Körfez",                  // Körfezler (s.53)
    "Boğaz",                   // Boğazlar
    "Yarımada",                // Yarımadalar
    // Geçitler
    "Geçit",                   // Dağ Geçitleri
    "Geçit / Tünel",           // Geçitler ve Tüneller
    // Akarsular & Göller
    "Akarsu",                  // Akarsular ve Havzalar (s.42-43)
    "Göl (Karstik)",           // Karstik Göller (s.45)
    "Göl (Volkanik Krater)",   // Volkanik Göller (s.46)
    "Göl (Tektonik)",          // Tektonik Göller (s.47-48)
    "Göl (Heyelan Set)",       // Heyelan Set Gölleri (s.49)
    "Göl (Lav Set)",           // Lav Set Gölleri
    "Göl (Volkanik Set)",      // Volkanik Set Gölleri (s.50)
    "Göl (Alüvyal Set)",       // Alüvyal Set Gölleri (s.51)
    "Göl (Alüvyon Set)",       // Alüvyon Set Gölleri (s.51)
    "Göl (Kıyı Set)",          // Kıyı Set Gölleri / Lagün (s.52)
    "Göl (Traverten Set)",     // Traverten Set Gölleri
    "Göl (Buzul)",             // Buzul Gölleri
    // Doğa Olayları & Koruma
    "Doğal Oluşum",            // Peribacaları, Şelaleler vb.
    "Şelale",                  // Şelaleler
    "Ramsar Alanı",            // Ramsar Alanları (s.74)
    "Milli Park",              // Milli Parklar (s.75-76)

    // ═══════════════════════════════════════
    // 👥 BEŞERİ (Ulaşım, Ticaret ve Turizm — Engin Hoca s.170-186)
    // ═══════════════════════════════════════
    // Ulaşım Türleri
    "Ulaşım (Demiryolu)",      // Demiryolu Ulaşımı
    "Ulaşım (YHT)",            // Yüksek Hızlı Tren (s.174)
    "Ulaşım (Köprü)",          // Köprüler (s.176)
    "Ulaşım (Tünel)",          // Tüneller (s.177)
    "Ulaşım (Havalimanı)",     // Havalimanları (s.178)
    "Ulaşım (Liman)",          // Yat Limanları / Yük Limanları (s.179-180)
    "Ulaşım (Kruvaziyer)",     // Kruvaziyer Limanlar (s.181)
    "Ulaşım (RO-RO)",          // RO-RO Hatları (s.183)
    "Liman",                   // Limanlar (genel)
    "Sınır Kapısı",            // Sınır Komşuları (s.187)
    // Turizm
    "Turizm (Kültür)",         // Kültür Turizmi
    "Turizm (Doğal)",          // Doğal Turizm
    "Turizm (Karma)",          // Karma Turizm
    "Turizm (İnanç)",          // İnanç Turizmi
    "UNESCO",                  // UNESCO Dünya Mirası (s.182)
    "Cittaslow",               // Sakin Şehirler
    "Kalkınma Projesi",        // Bölgesel Kalkınma Projeleri

    // ═══════════════════════════════════════
    // 💰 EKONOMİK — Tarım (Engin Hoca s.89-107)
    // ═══════════════════════════════════════
    "Tarım (Çay)",             // Çay Üretimi (s.91)
    "Tarım (Fındık)",          // Fındık Üretimi (s.89)
    "Tarım (Keten-Kenevir)",   // Keten Üretimi (s.96)
    "Tarım (Zeytin)",          // Zeytin Üretimi (s.98)
    "Tarım (Pirinç)",          // Pirinç Üretimi (s.99)
    "Tarım (Tütün)",           // Tütün Üretimi (s.102)
    "Tarım (İncir)",           // İncir Üretimi (s.103)
    "Tarım (Susam)",           // Susam Üretimi (s.103)
    "Tarım (Muz)",             // Muz Üretimi (s.105)
    "Tarım (Yer Fıstığı)",    // Yer Fıstığı Üretimi (s.106)
    "Tarım (Meyve)",           // Elma Üretimi (s.107)
    "Tarım (Turunçgil)",       // Turunçgil Üretimi (s.108)
    "Tarım (Kayısı)",          // Kayısı Üretimi (s.109)
    "Tarım (Kivi)",            // Kivi Üretimi (s.95)
    "Tarım (Antepfıstığı)",   // Antep Fıstığı (s.113)
    "Tarım (Pamuk)",           // Pamuk Üretimi (s.114)
    "Tarım (Buğday)",          // Buğday Üretimi (s.116)
    "Tarım (Şeker Pancarı)",   // Şeker Pancarı Üretimi (s.118)
    "Tarım (Aspir)",           // Aspir Üretimi (s.119)
    "Tarım (Mercimek)",        // Mercimek Üretimi (s.122)
    "Tarım (Üzüm)",           // Üzüm/Bağcılık
    "Tarım (Ayçiçeği)",       // Ayçiçeği Üretimi (s.93)
    "Tarım (Gül)",             // Gül Üretimi
    "Tarım (Sanayi)",          // Sanayi Bitkileri (genel)
    "Tarım (Mikroklima)",      // Mikroklima Tarımı

    // ═══════════════════════════════════════
    // 💰 EKONOMİK — Hayvancılık (Engin Hoca s.125-132)
    // ═══════════════════════════════════════
    "Tarım (Hayvancılık)",     // Büyükbaş/Küçükbaş (s.125-127)
    "Tarım (Bal)",             // Arıcılık (s.129)
    "Tarım (İpek)",            // İpek Böcekçiliği (s.130)

    // ═══════════════════════════════════════
    // 💰 EKONOMİK — Madenler (Engin Hoca s.133-155)
    // ═══════════════════════════════════════
    "Maden (Demir)",           // Demir Yatakları (s.133)
    "Maden (Bakır)",           // Bakır Yatakları (s.134)
    "Maden (Krom)",            // Krom Yatakları (s.135)
    "Maden (Boksit)",          // Boksit Yatakları (s.136)
    "Maden (Bor)",             // Bor Yatakları (s.137)
    "Maden (Mermer)",          // Mermer (s.138)
    "Maden (Barit)",           // Barit (s.139)
    "Maden (Tuz)",             // Tuz (s.139)
    "Maden (Fosfat)",          // Fosfat (s.140)
    "Maden (Zımpara)",         // Zımpara (s.140)
    "Maden (Altın)",           // Altın (s.142)
    "Maden (Kurşun-Çinko)",    // Kurşun-Çinko (s.144)
    "Maden (Trona)",           // Trona/Soda Külü (s.145)
    "Maden (Wolfram)",         // Wolfram/Tungsten (s.146)
    "Maden (Pomza)",           // Pomza (s.147)
    "Maden (Perlit)",          // Perlit (s.148)
    "Maden (Kükürt)",          // Kükürt/Manganez (s.149)
    "Maden (Manganez)",        // Manganez (s.149)
    "Maden (Linyit)",          // Linyit (s.150)
    "Maden (Taş Kömürü)",     // Taş Kömürü (s.150)
    "Maden (Petrol)",          // Petrol Yatakları (s.152)
    "Maden (Alüminyum)",       // Alüminyum
    "Maden (Nikel)",           // Nikel
    "Maden (Molibden)",        // Molibden
    "Maden (Cıva)",            // Cıva
    "Maden (Asfaltit)",        // Asfaltit (s.154)
    "Maden (Feldspat)",        // Feldspat
    "Maden (Bentonit)",        // Bentonit
    "Maden (Dolomit)",         // Dolomit
    "Maden (Süs Taşı)",       // Süs Taşı
    "Maden (Toryum)",          // Toryum (s.155)
    "Maden (Uranyum)",         // Uranyum (s.155)

    // ═══════════════════════════════════════
    // 💰 EKONOMİK — Enerji (Engin Hoca s.156-161)
    // ═══════════════════════════════════════
    "Enerji (Linyit)",         // Linyit Santralleri
    "Enerji (Taşkömürü)",     // Taş Kömürü Santralleri
    "Enerji (Termik)",         // Termik Santraller
    "Enerji (Petrol)",         // Petrol Boru Hatları (s.156)
    "Enerji (Doğal Gaz)",      // Doğal Gaz (s.153)
    "Enerji (Doğalgaz)",       // Doğalgaz (alternatif yazım)
    "Enerji (Boru Hattı)",     // Boru Hatları (s.156)
    "Enerji (LNG)",            // LNG Terminalleri
    "Baraj (HES)",             // Hidroelektrik Santralleri (s.158)
    "Baraj (Sulama)",          // Sulama Barajları
    "Enerji (Jeotermal)",      // Jeotermal Santraller (s.159)
    "Enerji (Rüzgar)",         // Rüzgar Santralleri (s.160)
    "Enerji (RES)",            // RES (alternatif)
    "Enerji (Güneş)",          // Güneş Enerjisi (s.161)
    "Enerji (Nükleer)",        // Nükleer Santraller
    "Enerji (Biyokütle)",      // Biyokütle
    "Enerji (Biyogaz)",        // Biyogaz

    // ═══════════════════════════════════════
    // 💰 EKONOMİK — Sanayi (Engin Hoca s.162-170)
    // ═══════════════════════════════════════
    "Sanayi (Şeker)",          // Çay ve Şeker Fabrikaları (s.162)
    "Sanayi (Kağıt)",          // Kağıt ve Mobilya Fabrikaları (s.164)
    "Sanayi (Tekstil)",        // Pamuklu Dokuma ve Halı-Kilim (s.165)
    "Sanayi (Halı)",           // Halı-Kilim
    "Sanayi (Sigara)",         // Tütün ve Sigara (s.166)
    "Sanayi (Sigara/Tütün)",   // Tütün/Sigara (alternatif)
    "Sanayi (Cam)",            // Beyaz Eşya ve Cam (s.167)
    "Sanayi (Kimya)",          // Deterjan Fabrikaları (s.168)
    "Sanayi (Gübre)",          // Gübre Fabrikaları
    "Sanayi (Deri)",           // Deri Sanayi (s.169)
    "Sanayi (Savunma)",        // Et ve Savunma (s.169)
    "Sanayi (Seramik)",        // Seramik ve Tuğla (s.170)
    "Sanayi (Gıda)",           // Gıda/Besin Sanayi
    "Sanayi (Demir-Çelik)",    // Demir Yolları Haritası (s.173)
    "Sanayi (Petrokimya)",     // Petrokimya
    "Sanayi (Otomotiv)",       // Otomotiv ve Demir Yolu (s.165)
    "Sanayi (Ulaşım)",        // Ulaşım Sanayi (Tersane, Vagon)
    "Sanayi (Metalurji)",      // Metalurji
    "Sanayi (Maden)",          // Maden Sanayi
    "Sanayi (Mobilya)",        // Mobilya Sanayi
    "Sanayi (Orman Ürünleri)", // Orman Ürünleri
    "Sanayi (OSB)",            // Organize Sanayi Bölgeleri
];

/**
 * Yardımcı: Verilen alt_kategori listesini ünite başlıkları altında gruplar.
 * Sidebar'da 3 katmanlı görünüm için kullanılır.
 * Dönen map: ünite başlığı → alt_kategori dizisi (Engin Hoca kitap sırasına göre sıralı)
 */
export function groupAltKategorisByUnite(altKategoriler: string[]): Map<string, string[]> {
    const tempMap: Record<string, string[]> = {};
    for (const alt of altKategoriler) {
        const unite = ALT_KATEGORI_TO_UNITE[alt] || 'Diğer';
        if (!tempMap[unite]) tempMap[unite] = [];
        tempMap[unite].push(alt);
    }
    
    // EXAM_BLUEPRINTS sırasına göre üniteleri sırala
    const blueprintOrder = EXAM_BLUEPRINTS.geography.map(bp => bp.topic);
    const sortedKeys = Object.keys(tempMap).sort((a, b) => {
        const idxA = blueprintOrder.indexOf(a);
        const idxB = blueprintOrder.indexOf(b);
        if (idxA === -1 && idxB === -1) return a.localeCompare(b, 'tr');
        if (idxA === -1) return 1;
        if (idxB === -1) return -1;
        return idxA - idxB;
    });
    
    // Alt kategorileri Engin Hoca kitap sırasına göre sırala
    const grouped = new Map<string, string[]>();
    for (const key of sortedKeys) {
        grouped.set(key, tempMap[key].sort((a, b) => {
            const idxA = GEOGRAPHY_ALT_KATEGORI_SIRASI.indexOf(a);
            const idxB = GEOGRAPHY_ALT_KATEGORI_SIRASI.indexOf(b);
            // Sıralama dizisinde bulunamayanlar sona
            if (idxA === -1 && idxB === -1) return a.localeCompare(b, 'tr');
            if (idxA === -1) return 1;
            if (idxB === -1) return -1;
            return idxA - idxB;
        }));
    }
    return grouped;
}


// Tüm stats, zayıf konu ve soru eşleştirmesi bu fonksiyonla tutarlı olur.
// ═══════════════════════════════════════════════════════════════

/** Türkçe-safe lowercase — İ/I sorununu düzeltmek için ÖNCE Türkçe replace, SONRA toLowerCase */
const trLower = (s: string) => s
    .replace(/İ/g, 'i').replace(/I/g, 'ı')
    .replace(/Ş/g, 'ş').replace(/Ç/g, 'ç')
    .replace(/Ü/g, 'ü').replace(/Ö/g, 'ö')
    .replace(/Ğ/g, 'ğ')
    .toLowerCase()
    .replace(/i̇/g, 'i'); // combining dot cleanup (some engines)

/** Bir metinden anlamlı kelimeleri çıkar (3+ karakter) */
const extractWords = (s: string) =>
    trLower(s).split(/[\s,\-—()/]+/).filter(w => w.length >= 3);

/**
 * Repository topic → Blueprint topic eşleştirme haritası.
 * AI'ın üretebileceği TÜM alt-konu isimlerini blueprint konusuna eşler.
 * Bu alias map, normalizer'ın ilk adımında kullanılır.
 */
const TOPIC_ALIASES: Record<string, Record<string, string>> = {
    geography: {
        // ═══════════════════════════════════════════════════════════════
        // FİZİKİ ÖZELLİKLER — RAG: 03-yersekilleri.md + db.ts alt_kategorileri
        // ═══════════════════════════════════════════════════════════════
        "yerşekilleri": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'nin yerşekilleri": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'nin yerşekilleri (dağlar, ovalar, platolar)": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'nin yeryüzü şekilleri": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'nin yeryüzü şekilleri ve jeolojik oluşumu": "Türkiye'nin Fiziki Özellikleri",
        "dağlar": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'nin dağları": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'nin dağları ve oluşum tipleri": "Türkiye'nin Fiziki Özellikleri",
        "kıvrım dağ": "Türkiye'nin Fiziki Özellikleri",
        "kırık dağ": "Türkiye'nin Fiziki Özellikleri",
        "volkanik dağ": "Türkiye'nin Fiziki Özellikleri",
        "volkanik dağlar": "Türkiye'nin Fiziki Özellikleri",
        "masif arazi": "Türkiye'nin Fiziki Özellikleri",
        "ovalar": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'nin ovaları": "Türkiye'nin Fiziki Özellikleri",
        "delta ovası": "Türkiye'nin Fiziki Özellikleri",
        "tektonik ova": "Türkiye'nin Fiziki Özellikleri",
        "karstik ova": "Türkiye'nin Fiziki Özellikleri",
        "dağ eteği ovası": "Türkiye'nin Fiziki Özellikleri",
        "volkanik ova": "Türkiye'nin Fiziki Özellikleri",
        "platolar": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'nin platoları": "Türkiye'nin Fiziki Özellikleri",
        "lav platoları": "Türkiye'nin Fiziki Özellikleri",
        "karstik platolar": "Türkiye'nin Fiziki Özellikleri",
        "aşınım platoları": "Türkiye'nin Fiziki Özellikleri",
        "aşınım platosu": "Türkiye'nin Fiziki Özellikleri",
        "tabaka düzlüğü": "Türkiye'nin Fiziki Özellikleri",
        "yatay plato": "Türkiye'nin Fiziki Özellikleri",
        "akarsular": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'nin akarsuları": "Türkiye'nin Fiziki Özellikleri",
        "akarsu havzaları": "Türkiye'nin Fiziki Özellikleri",
        "kapalı havzalar": "Türkiye'nin Fiziki Özellikleri",
        "su varlığı": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'nin su varlığı": "Türkiye'nin Fiziki Özellikleri",
        "göller": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'nin gölleri": "Türkiye'nin Fiziki Özellikleri",
        "doğal göller": "Türkiye'nin Fiziki Özellikleri",
        "baraj gölleri": "Türkiye'nin Fiziki Özellikleri",
        "tektonik göl": "Türkiye'nin Fiziki Özellikleri",
        "volkanik göl": "Türkiye'nin Fiziki Özellikleri",
        "karstik göl": "Türkiye'nin Fiziki Özellikleri",
        "heyelan set gölü": "Türkiye'nin Fiziki Özellikleri",
        "kıyı set gölü": "Türkiye'nin Fiziki Özellikleri",
        "lagün": "Türkiye'nin Fiziki Özellikleri",
        "su, toprak ve doğal varlıklar": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'de su, toprak ve doğal varlıklar": "Türkiye'nin Fiziki Özellikleri",
        "bölgeler ve bölgesel kalkınma projeleri": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'nin bölgesel kalkınma projeleri": "Türkiye'nin Fiziki Özellikleri",
        "bölgeler coğrafyası": "Türkiye'nin Fiziki Özellikleri",
        "bölgeler": "Türkiye'nin Fiziki Özellikleri",
        "kalkınma projesi": "Türkiye'nin Fiziki Özellikleri",
        "gap": "Türkiye'nin Fiziki Özellikleri",
        "dap": "Türkiye'nin Fiziki Özellikleri",
        "dokap": "Türkiye'nin Fiziki Özellikleri",
        "kop": "Türkiye'nin Fiziki Özellikleri",
        "jeolojik yapı": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'nin jeolojik yapısı": "Türkiye'nin Fiziki Özellikleri",
        "jeolojik zaman": "Türkiye'nin Fiziki Özellikleri",
        "deprem": "Türkiye'nin Fiziki Özellikleri",
        "fay hatları": "Türkiye'nin Fiziki Özellikleri",
        "kıyılar": "Türkiye'nin Fiziki Özellikleri",
        "kıyı şekli": "Türkiye'nin Fiziki Özellikleri",
        "kıyı tipleri": "Türkiye'nin Fiziki Özellikleri",
        "tombolo": "Türkiye'nin Fiziki Özellikleri",
        "falez": "Türkiye'nin Fiziki Özellikleri",
        "körfez": "Türkiye'nin Fiziki Özellikleri",
        "körfezler": "Türkiye'nin Fiziki Özellikleri",
        "boğaz": "Türkiye'nin Fiziki Özellikleri",
        "boğazlar": "Türkiye'nin Fiziki Özellikleri",
        "yarımada": "Türkiye'nin Fiziki Özellikleri",
        "yarımadalar": "Türkiye'nin Fiziki Özellikleri",
        "şelale": "Türkiye'nin Fiziki Özellikleri",
        "milli park": "Türkiye'nin Fiziki Özellikleri",
        "ramsar alanı": "Türkiye'nin Fiziki Özellikleri",
        "doğal oluşum": "Türkiye'nin Fiziki Özellikleri",
        "mağara": "Türkiye'nin Fiziki Özellikleri",
        "toprak": "Türkiye'nin Fiziki Özellikleri",
        "toprak tipleri": "Türkiye'nin Fiziki Özellikleri",
        "toprak çeşitleri": "Türkiye'nin Fiziki Özellikleri",
        "toprak oluşumu": "Türkiye'nin Fiziki Özellikleri",
        "toprak horizonları": "Türkiye'nin Fiziki Özellikleri",
        "zonal topraklar": "Türkiye'nin Fiziki Özellikleri",
        "azonal topraklar": "Türkiye'nin Fiziki Özellikleri",
        "intrazonal topraklar": "Türkiye'nin Fiziki Özellikleri",
        "terra rossa": "Türkiye'nin Fiziki Özellikleri",
        "doğal afetler": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'de doğal afetler": "Türkiye'nin Fiziki Özellikleri",
        "afetler": "Türkiye'nin Fiziki Özellikleri",
        "dış kuvvetler": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'de dış kuvvetler": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'de dış kuvvetler ve yer şekilleri": "Türkiye'nin Fiziki Özellikleri",
        "iç kuvvetler": "Türkiye'nin Fiziki Özellikleri",
        "erozyon": "Türkiye'nin Fiziki Özellikleri",
        "heyelan": "Türkiye'nin Fiziki Özellikleri",
        "çığ": "Türkiye'nin Fiziki Özellikleri",
        "orman yangınları": "Türkiye'nin Fiziki Özellikleri",
        "volkanizma": "Türkiye'nin Fiziki Özellikleri",
        "karstik şekiller": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'de karstik şekiller": "Türkiye'nin Fiziki Özellikleri",
        "buzul şekilleri": "Türkiye'nin Fiziki Özellikleri",
        "rüzgar şekilleri": "Türkiye'nin Fiziki Özellikleri",
        "dalga şekilleri": "Türkiye'nin Fiziki Özellikleri",
        "kaynak suları": "Türkiye'nin Fiziki Özellikleri",
        "geçitler": "Türkiye'nin Fiziki Özellikleri",
        "türkiye'nin geçitleri": "Türkiye'nin Fiziki Özellikleri",
        "doğal geçitler": "Türkiye'nin Fiziki Özellikleri",
        "tüneller": "Türkiye'nin Fiziki Özellikleri",
        "geçit": "Türkiye'nin Fiziki Özellikleri",
        "dağ geçitleri": "Türkiye'nin Fiziki Özellikleri",
        "dağ geçitleri ve tüneller": "Türkiye'nin Fiziki Özellikleri",
        // Harita noktası alt kategorileri (db.ts): "Göl (Tektonik)", "Plato (Volkanik)", "Fay Hattı", "Deniz"...
        "göl": "Türkiye'nin Fiziki Özellikleri",
        "plato": "Türkiye'nin Fiziki Özellikleri",
        "fay hattı": "Türkiye'nin Fiziki Özellikleri",
        "kıyı düzlüğü": "Türkiye'nin Fiziki Özellikleri",
        "deniz": "Türkiye'nin Fiziki Özellikleri",
        "denizler": "Türkiye'nin Fiziki Özellikleri",

        // ═══════════════════════════════════════════════════════════════
        // COĞRAFİ KONUM — RAG: 01-cografi-konum.md
        // ═══════════════════════════════════════════════════════════════
        "koordinat sistemi": "Türkiye'nin Coğrafi Konumu",
        "koordinat": "Türkiye'nin Coğrafi Konumu",
        "paralel ve meridyenler": "Türkiye'nin Coğrafi Konumu",
        "paraleller": "Türkiye'nin Coğrafi Konumu",
        "paralel daireler": "Türkiye'nin Coğrafi Konumu",
        "meridyenler": "Türkiye'nin Coğrafi Konumu",
        "meridyen": "Türkiye'nin Coğrafi Konumu",
        "enlem-boylam": "Türkiye'nin Coğrafi Konumu",
        "enlem ve boylam": "Türkiye'nin Coğrafi Konumu",
        "matematik konum": "Türkiye'nin Coğrafi Konumu",
        "mutlak konum": "Türkiye'nin Coğrafi Konumu",
        "mutlak konum ve uç noktalar": "Türkiye'nin Coğrafi Konumu",
        "mutlak konumun sonuçları": "Türkiye'nin Coğrafi Konumu",
        "türkiye'nin mutlak konumunun sonuçları": "Türkiye'nin Coğrafi Konumu",
        "göreceli konum": "Türkiye'nin Coğrafi Konumu",
        "türkiye'nin göreceli konumunun sonuçları": "Türkiye'nin Coğrafi Konumu",
        "özel çizgiler": "Türkiye'nin Coğrafi Konumu",
        "yerel saat": "Türkiye'nin Coğrafi Konumu",
        "saat farkı": "Türkiye'nin Coğrafi Konumu",
        "türkiye'nin komşuları": "Türkiye'nin Coğrafi Konumu",
        "komşu ülkeler": "Türkiye'nin Coğrafi Konumu",
        "coğrafi konum": "Türkiye'nin Coğrafi Konumu",
        "türkiye'nin konumu": "Türkiye'nin Coğrafi Konumu",
        "dünyanın hareketleri": "Türkiye'nin Coğrafi Konumu",
        "dünyanın şekli": "Türkiye'nin Coğrafi Konumu",
        "dünyanın eksen eğikliği": "Türkiye'nin Coğrafi Konumu",
        "dünya'nın hareketleri": "Türkiye'nin Coğrafi Konumu",
        "dünya'nın yörüngesinin elips şekli": "Türkiye'nin Coğrafi Konumu",
        "mevsimler": "Türkiye'nin Coğrafi Konumu",
        "gece-gündüz süre farkı": "Türkiye'nin Coğrafi Konumu",
        "güneş ışınlarının geliş açısı": "Türkiye'nin Coğrafi Konumu",
        "harita bilgisi": "Türkiye'nin Coğrafi Konumu",
        "harita çeşitleri": "Türkiye'nin Coğrafi Konumu",
        "harita": "Türkiye'nin Coğrafi Konumu",
        "ölçek": "Türkiye'nin Coğrafi Konumu",
        "izohips": "Türkiye'nin Coğrafi Konumu",
        "projeksiyon": "Türkiye'nin Coğrafi Konumu",
        "coğrafi keşifler": "Türkiye'nin Coğrafi Konumu",
        "sınır kapıları": "Türkiye'nin Coğrafi Konumu",
        "sınır kapısı": "Türkiye'nin Coğrafi Konumu",
        "türkiye'nin sınır kapıları": "Türkiye'nin Coğrafi Konumu",
        "türkiye'nin sınırları": "Türkiye'nin Coğrafi Konumu",
        "türkiye'nin sınırları ve komşuları": "Türkiye'nin Coğrafi Konumu",
        "sınır komşuları": "Türkiye'nin Coğrafi Konumu",
        "kara sınırları": "Türkiye'nin Coğrafi Konumu",
        "kara sınırı": "Türkiye'nin Coğrafi Konumu",
        "kara sınırları ve komşular": "Türkiye'nin Coğrafi Konumu",
        "sınır kapıları ve ulaşım": "Türkiye'nin Coğrafi Konumu",
        "gerçek ve iz düşüm alan": "Türkiye'nin Coğrafi Konumu",
        "iz düşüm alan": "Türkiye'nin Coğrafi Konumu",
        "uç noktalar": "Türkiye'nin Coğrafi Konumu",
        "boylam": "Türkiye'nin Coğrafi Konumu",
        "boylamın etkileri": "Türkiye'nin Coğrafi Konumu",
        "enlem": "Türkiye'nin Coğrafi Konumu",
        "enlemin etkileri": "Türkiye'nin Coğrafi Konumu",
        "özel tarihler": "Türkiye'nin Coğrafi Konumu",
        "özel tarihler ve gündüz süreleri": "Türkiye'nin Coğrafi Konumu",
        "bakı etkisi": "Türkiye'nin Coğrafi Konumu",
        "bakı ve gölge yönü": "Türkiye'nin Coğrafi Konumu",

        // ═══════════════════════════════════════════════════════════════
        // İKLİM — RAG: 02-iklim-bitki-ortusu.md
        // ═══════════════════════════════════════════════════════════════
        "iklim tipleri": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "iklim elemanları": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "iklimi etkileyen faktörler": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "bitki örtüsü": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "bitki örtüsü sınıfları": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "sıcaklık": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "sıcaklık ve enlem ilişkisi": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "yağış": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "nem ve yağış": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "rüzgarlar": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "rüzgârlar": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "basınç": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "akdeniz iklimi": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "karadeniz iklimi": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "karasal iklim": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "step iklimi": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "sert karasal iklim": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "marmara iklimi": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "geçiş iklimi": "Türkiye'nin İklimi ve Bitki Örtüsü",

        // ═══════════════════════════════════════════════════════════════
        // NÜFUS — RAG: 04-nufus-yerlesme.md
        // ═══════════════════════════════════════════════════════════════
        "nüfus": "Türkiye'de Nüfus ve Yerleşme",
        "nüfus artış hızı": "Türkiye'de Nüfus ve Yerleşme",
        "nüfus politikaları": "Türkiye'de Nüfus ve Yerleşme",
        "nüfus yoğunluğu": "Türkiye'de Nüfus ve Yerleşme",
        "nüfus piramidi": "Türkiye'de Nüfus ve Yerleşme",
        "nüfus sayımları": "Türkiye'de Nüfus ve Yerleşme",
        "nüfusun dağılışı": "Türkiye'de Nüfus ve Yerleşme",
        "göç": "Türkiye'de Nüfus ve Yerleşme",
        "iç göçler": "Türkiye'de Nüfus ve Yerleşme",
        "dış göçler": "Türkiye'de Nüfus ve Yerleşme",
        "göçler": "Türkiye'de Nüfus ve Yerleşme",
        "yerleşme": "Türkiye'de Nüfus ve Yerleşme",
        "yerleşme tipleri": "Türkiye'de Nüfus ve Yerleşme",
        "konut tipleri": "Türkiye'de Nüfus ve Yerleşme",
        "şehirleşme": "Türkiye'de Nüfus ve Yerleşme",
        "kentleşme": "Türkiye'de Nüfus ve Yerleşme",
        "kır yerleşmeleri": "Türkiye'de Nüfus ve Yerleşme",
        "kent yerleşmeleri": "Türkiye'de Nüfus ve Yerleşme",

        // ═══════════════════════════════════════════════════════════════
        // TARIM — RAG: 05-tarim.md
        // ═══════════════════════════════════════════════════════════════
        "tarım": "Türkiye'de Tarım",
        "tarım ürünleri": "Türkiye'de Tarım",
        "tarım yöntemleri": "Türkiye'de Tarım",
        "tarımı etkileyen faktörler": "Türkiye'de Tarım",
        "türkiye'de tarım, hayvancılık ve ormancılık": "Türkiye'de Tarım",
        "bitkisel üretim": "Türkiye'de Tarım",
        "tahıllar": "Türkiye'de Tarım",
        "sanayi bitkileri": "Türkiye'de Tarım",
        "baklagiller": "Türkiye'de Tarım",
        "yağ bitkileri": "Türkiye'de Tarım",
        "meyvecilik": "Türkiye'de Tarım",
        "yumrulu bitkiler": "Türkiye'de Tarım",
        "sulama": "Türkiye'de Tarım",

        // ═══════════════════════════════════════════════════════════════
        // HAYVANCILIK — RAG: 06-hayvancilik.md
        // ═══════════════════════════════════════════════════════════════
        "hayvancılık": "Türkiye'de Hayvancılık",
        "küçükbaş hayvancılık": "Türkiye'de Hayvancılık",
        "büyükbaş hayvancılık": "Türkiye'de Hayvancılık",
        "kümes hayvancılığı": "Türkiye'de Hayvancılık",
        "arıcılık": "Türkiye'de Hayvancılık",
        "ipek böcekçiliği": "Türkiye'de Hayvancılık",
        "ormancılık": "Türkiye'de Hayvancılık",
        "balıkçılık": "Türkiye'de Hayvancılık",

        // ═══════════════════════════════════════════════════════════════
        // MADENLER & ENERJİ — RAG: 07-madenler-enerji.md + db.ts
        // ═══════════════════════════════════════════════════════════════
        "madenler": "Türkiye'de Madenler ve Enerji Kaynakları",
        "türkiye'de madenler": "Türkiye'de Madenler ve Enerji Kaynakları",
        "enerji kaynakları": "Türkiye'de Madenler ve Enerji Kaynakları",
        "türkiye'de enerji kaynakları": "Türkiye'de Madenler ve Enerji Kaynakları",
        "enerji": "Türkiye'de Madenler ve Enerji Kaynakları",
        "enerji hatları": "Türkiye'de Madenler ve Enerji Kaynakları",
        "boru hatları": "Türkiye'de Madenler ve Enerji Kaynakları",
        "enerji taşıma hatları": "Türkiye'de Madenler ve Enerji Kaynakları",
        "maden": "Türkiye'de Madenler ve Enerji Kaynakları",
        "demir": "Türkiye'de Madenler ve Enerji Kaynakları",
        "bakır": "Türkiye'de Madenler ve Enerji Kaynakları",
        "krom": "Türkiye'de Madenler ve Enerji Kaynakları",
        "bor": "Türkiye'de Madenler ve Enerji Kaynakları",
        "boksit": "Türkiye'de Madenler ve Enerji Kaynakları",
        "linyit": "Türkiye'de Madenler ve Enerji Kaynakları",
        "taş kömürü": "Türkiye'de Madenler ve Enerji Kaynakları",
        "petrol": "Türkiye'de Madenler ve Enerji Kaynakları",
        "doğal gaz": "Türkiye'de Madenler ve Enerji Kaynakları",
        "hidroelektrik": "Türkiye'de Madenler ve Enerji Kaynakları",
        "jeotermal enerji": "Türkiye'de Madenler ve Enerji Kaynakları",
        "nükleer enerji": "Türkiye'de Madenler ve Enerji Kaynakları",
        "güneş enerjisi": "Türkiye'de Madenler ve Enerji Kaynakları",
        "rüzgar enerjisi": "Türkiye'de Madenler ve Enerji Kaynakları",
        "baraj": "Türkiye'de Madenler ve Enerji Kaynakları",

        // ═══════════════════════════════════════════════════════════════
        // SANAYİ — RAG: 08-sanayi.md + db.ts
        // ═══════════════════════════════════════════════════════════════
        "sanayi": "Türkiye'de Sanayi",
        "endüstri": "Türkiye'de Sanayi",
        "sanayileşme": "Türkiye'de Sanayi",
        "sanayi kolları": "Türkiye'de Sanayi",
        "besin sanayi": "Türkiye'de Sanayi",
        "gıda sanayi": "Türkiye'de Sanayi",
        "dokuma sanayi": "Türkiye'de Sanayi",
        "tekstil": "Türkiye'de Sanayi",
        "demir-çelik": "Türkiye'de Sanayi",
        "otomotiv": "Türkiye'de Sanayi",
        "kimya endüstrisi": "Türkiye'de Sanayi",
        "makine sanayi": "Türkiye'de Sanayi",
        "maden sanayi": "Türkiye'de Sanayi",

        // ═══════════════════════════════════════════════════════════════
        // ULAŞIM, TİCARET, TURİZM — RAG: 09-ulasim-ticaret-turizm.md
        // ═══════════════════════════════════════════════════════════════
        "ulaşım": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "demiryolu": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "demiryolları": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "bölgesel coğrafya": "Türkiye'nin Fiziki Özellikleri",
        "ticaret": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "turizm": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "dış ticaret": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "iç ticaret": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "türkiye'de ulaşım, ticaret ve turizm": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "deniz yolu ulaşımı": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "demir yolu ulaşımı": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "kara yolu ulaşımı": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "hava yolu ulaşımı": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "liman": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "serbest ticaret bölgeleri": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "kültür turizmi": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "unesco": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "cittaslow": "Türkiye'de Ulaşım, Ticaret ve Turizm",

        // ═══════════════════════════════════════════════════════════════
        // GENEL / LEGACY — Eski global_questions verisindeki serbest topic'ler
        // ═══════════════════════════════════════════════════════════════
        "su coğrafyası": "Türkiye'nin Fiziki Özellikleri",
        "ekonomik coğrafya": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "nüfus coğrafyası": "Türkiye'de Nüfus ve Yerleşme",
        "türkiye'nin jeolojik oluşumu": "Türkiye'nin Fiziki Özellikleri",
        "iklim ve rüzgarlar": "Türkiye'nin İklimi ve Bitki Örtüsü",

        // ═══════════════════════════════════════════════════════════════
        // ZİHİN AĞI DÜĞÜM KİMLİKLERİ (MIND MAP NODE IDS & ALIASES)
        // ═══════════════════════════════════════════════════════════════
        "cografi_konum": "Türkiye'nin Coğrafi Konumu",
        "cografi konum": "Türkiye'nin Coğrafi Konumu",
        "fiziki_ozellikler": "Türkiye'nin Fiziki Özellikleri",
        "fiziki ozellikler": "Türkiye'nin Fiziki Özellikleri",
        "fiziki özellikler": "Türkiye'nin Fiziki Özellikleri",
        "yersekilleri": "Türkiye'nin Fiziki Özellikleri",
        "su_toprak": "Türkiye'nin Fiziki Özellikleri",
        "bolgeler": "Türkiye'nin Fiziki Özellikleri",
        "iklim_bitki": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "iklim ve bitki örtüsü": "Türkiye'nin İklimi ve Bitki Örtüsü",
        "nufus": "Türkiye'de Nüfus ve Yerleşme",
        "nufus_yerlesme": "Türkiye'de Nüfus ve Yerleşme",
        "nüfus & yerleşme": "Türkiye'de Nüfus ve Yerleşme",
        "tarim": "Türkiye'de Tarım",
        "hayvancilik": "Türkiye'de Hayvancılık",
        "madenler_enerji": "Türkiye'de Madenler ve Enerji Kaynakları",
        "maden_enerji": "Türkiye'de Madenler ve Enerji Kaynakları",
        "maden & enerji": "Türkiye'de Madenler ve Enerji Kaynakları",
        "ulasim_ticaret": "Türkiye'de Ulaşım, Ticaret ve Turizm",
        "ulasim & ticaret": "Türkiye'de Ulaşım, Ticaret ve Turizm",
    },
    citizenship: {
        "hukuk": "Hukukun Temel Kavramları",
        "hukukun kaynakları": "Hukukun Temel Kavramları",
        "hukuk kuralları": "Hukukun Temel Kavramları",
        "hukukun dalları": "Hukukun Temel Kavramları",
        "hak ve fiil ehliyeti": "Hukukun Temel Kavramları",
        "kişi hukuku": "Hukukun Temel Kavramları",
        "yaptırım": "Hukukun Temel Kavramları",

        "anayasa tarihi": "Türk Anayasa Tarihi",
        "osmanlı anayasaları": "Türk Anayasa Tarihi",
        "sened-i ittifak": "Türk Anayasa Tarihi",
        "tanzimat": "Türk Anayasa Tarihi",
        "kanun-i esasi": "Türk Anayasa Tarihi",
        "1921 anayasası": "Türk Anayasa Tarihi",
        "1924 anayasası": "Türk Anayasa Tarihi",
        "1961 anayasası": "Türk Anayasa Tarihi",

        "1982 anayasası": "1982 Anayasası ve Temel Esaslar",
        "anayasa değişiklikleri": "1982 Anayasası ve Temel Esaslar",
        "devletin temel nitelikleri": "1982 Anayasası ve Temel Esaslar",
        "laiklik": "1982 Anayasası ve Temel Esaslar",
        "cumhuriyetin nitelikleri": "1982 Anayasası ve Temel Esaslar",

        "temel haklar": "Temel Hak ve Hürriyetler",
        "kişi hakları": "Temel Hak ve Hürriyetler",
        "sosyal haklar": "Temel Hak ve Hürriyetler",
        "siyasi haklar": "Temel Hak ve Hürriyetler",
        "hak ve özgürlükler": "Temel Hak ve Hürriyetler",
        "temel hak ve hürriyet": "Temel Hak ve Hürriyetler",

        "tbmm": "Yasama",
        "milletvekili": "Yasama",
        "kanun yapımı": "Yasama",
        "meclis": "Yasama",

        "cumhurbaşkanı": "Yürütme",
        "bakanlar kurulu": "Yürütme",
        "cumhurbaşkanlığı kararnamesi": "Yürütme",

        "mahkeme": "Yargı",
        "anayasa mahkemesi": "Yargı",
        "yargıtay": "Yargı",
        "danıştay": "Yargı",
        "hakimlik": "Yargı",
        "savcılık": "Yargı",

        "idare": "İdare Hukuku",
        "belediye": "İdare Hukuku",
        "valilik": "İdare Hukuku",
        "merkezi yönetim": "İdare Hukuku",
        "yerel yönetim": "İdare Hukuku",

        "uluslararası": "Güncel Bilgiler",
        "uluslararası kuruluşlar": "Güncel Bilgiler",
        "uluslararasi": "Güncel Bilgiler",
        "uluslararasi kuruluslar": "Güncel Bilgiler",
        "nato": "Güncel Bilgiler",
        "birleşmiş milletler": "Güncel Bilgiler",
        "avrupa birliği": "Güncel Bilgiler",
        "unesco": "Güncel Bilgiler",
        "türksoy": "Güncel Bilgiler",
        "türksav": "Güncel Bilgiler",
        "nobel": "Güncel Bilgiler",
        "sanat": "Güncel Bilgiler",
        "sanat tarihi": "Güncel Bilgiler",
        "edebiyat": "Güncel Bilgiler",

        "güncel": "Güncel Bilgiler",
        "güncel bilgiler": "Güncel Bilgiler",
        "guncel": "Güncel Bilgiler",
        "guncel bilgiler": "Güncel Bilgiler",
        "genel kültür": "Güncel Bilgiler",
        "genel kultur": "Güncel Bilgiler",

        // Zihin Ağı Düğüm Kimlikleri
        "cit_hukuk_temel": "Hukukun Temel Kavramları",
        "cit_anayasa_tarihi": "Türk Anayasa Tarihi",
        "cit_1982": "1982 Anayasası ve Temel Esaslar",
        "cit_haklar": "Temel Hak ve Hürriyetler",
        "cit_yasama": "Yasama",
        "cit_yurutme": "Yürütme",
        "cit_yargi": "Yargı",
        "cit_idare": "İdare Hukuku",
        "cit_uluslararasi": "Güncel Bilgiler",
        "cit_guncel": "Güncel Bilgiler",
    },
    turkish: {
        "sözcükte anlam": "Sözcükte Anlam",
        "anlam": "Sözcükte Anlam",
        "eş anlamlı": "Sözcükte Anlam",
        "zıt anlamlı": "Sözcükte Anlam",
        "mecaz anlam": "Sözcükte Anlam",
        "gerçek anlam": "Sözcükte Anlam",
        "terim anlam": "Sözcükte Anlam",
        "deyim": "Sözcükte Anlam",
        "deyimler ve atasözleri": "Sözcükte Anlam",
        "söz öbekleri": "Sözcükte Anlam",
        "sözcük öbekleri": "Sözcükte Anlam",
        "sözcüğün ve sözün anlamı": "Sözcükte Anlam",
        "sözcüğün anlam açılımı": "Sözcükte Anlam",
        "temel anlam": "Sözcükte Anlam",
        "yan anlam": "Sözcükte Anlam",
        "sözcükler arası anlam ilişkileri": "Sözcükte Anlam",
        "eş anlamlı sözcükler": "Sözcükte Anlam",
        "eş sesli sözcükler": "Sözcükte Anlam",
        "sesteş sözcükler": "Sözcükte Anlam",
        "karşıt anlamlı sözcükler": "Sözcükte Anlam",
        "yakın anlamlı sözcükler": "Sözcükte Anlam",
        "genel - özel anlamlı sözcükler": "Sözcükte Anlam",
        "nitel - nicel anlamlı sözcükler": "Sözcükte Anlam",
        "somut - soyut anlamlı sözcükler": "Sözcükte Anlam",
        "sözcüğe mecaz anlam kazandırma yolları": "Sözcükte Anlam",
        "güzel adlandırma": "Sözcükte Anlam",
        "dolaylama": "Sözcükte Anlam",
        "dokundurma": "Sözcükte Anlam",
        "tariz": "Sözcükte Anlam",
        "iğneleme": "Sözcükte Anlam",
        "kinaye": "Sözcükte Anlam",
        "değinmece": "Sözcükte Anlam",
        "ad aktarması": "Sözcükte Anlam",
        "mecazımürsel": "Sözcükte Anlam",
        "aktarmalar": "Sözcükte Anlam",
        "duyular arası aktarma": "Sözcükte Anlam",
        "benzetme": "Sözcükte Anlam",
        "yansıma sözcükler": "Sözcükte Anlam",
        "kalıplaşmamış söz öbekleri": "Sözcükte Anlam",

        "cümlede anlam": "Cümlede Anlam",
        "cümle anlamı": "Cümlede Anlam",
        "cümle yorumlama": "Cümlede Anlam",
        "cümle yorumu": "Cümlede Anlam",
        "kesin yargı": "Cümlede Anlam",
        "örtülü anlam": "Cümlede Anlam",
        "cümle tamamlama": "Cümlede Anlam",
        "cümle oluşturma": "Cümlede Anlam",

        "paragraf": "Paragraf",
        "paragrafta anlam": "Paragraf",
        "paragrafta yapı": "Paragraf",
        "paragrafta anlam ve yapı": "Paragraf",
        "ana düşünce": "Paragraf",
        "yardımcı düşünce": "Paragraf",
        "yardımcı düşünceler": "Paragraf",
        "paragraf tamamlama": "Paragraf",
        "akışı bozan cümle": "Paragraf",
        "paragraf bölme": "Paragraf",
        "anlatım teknikleri": "Paragraf",
        "anlatım özellikleri": "Paragraf",

        "biçim bilgisi": "Biçim Bilgisi",
        "sözcükte yapı": "Biçim Bilgisi",
        "ek": "Biçim Bilgisi",
        "kök": "Biçim Bilgisi",
        "kökler ve ekler": "Biçim Bilgisi",
        "yapım ekleri": "Biçim Bilgisi",
        "çekim ekleri": "Biçim Bilgisi",
        "türetme": "Biçim Bilgisi",
        "gövde": "Biçim Bilgisi",
        "sözcüğün yapısı": "Biçim Bilgisi",

        "ses bilgisi": "Ses Bilgisi",
        "ünlü uyumu": "Ses Bilgisi",
        "ünlü düşmesi": "Ses Bilgisi",
        "ünsüz benzeşmesi": "Ses Bilgisi",
        "ünsüz yumuşaması": "Ses Bilgisi",
        "ünlü daralması": "Ses Bilgisi",
        "ses olayları": "Ses Bilgisi",
        "ulama": "Ses Bilgisi",

        "yazım kuralları": "Yazım Kuralları",
        "yazım": "Yazım Kuralları",
        "imla": "Yazım Kuralları",
        "büyük harf": "Yazım Kuralları",
        "büyük harfler": "Yazım Kuralları",
        "birleşik sözcükler": "Yazım Kuralları",
        "de ve ki nin yazımı": "Yazım Kuralları",
        "sayıların yazımı": "Yazım Kuralları",
        "bitişik-ayrı yazım": "Yazım Kuralları",
        "yazım yanlışları": "Yazım Kuralları",
        "ikilemeler ve deyimlerin yazılışı": "Yazım Kuralları",
        "ikilemelerin yazılışı": "Yazım Kuralları",
        "deyimlerin yazılışı": "Yazım Kuralları",
        "pekiştirmeli sözlerin yazılışı": "Yazım Kuralları",
        "\"mı, mi\" soru ekinin yazılışı": "Yazım Kuralları",
        "mı, mi soru ekinin yazılışı": "Yazım Kuralları",
        "soru ekinin yazılışı": "Yazım Kuralları",
        "\"da, de\" bağlacı ve hâl ekinin yazılışı": "Yazım Kuralları",
        "da, de bağlacı ve hâl ekinin yazılışı": "Yazım Kuralları",
        "da, de bağlacının yazılışı": "Yazım Kuralları",
        "\"ki\" bağlacının ve \"-ki\" ekinin yazılışı": "Yazım Kuralları",
        "ki bağlacının ve -ki ekinin yazılışı": "Yazım Kuralları",
        "ki bağlacının yazılışı": "Yazım Kuralları",
        "sayıların ve tarihlerin yazılışı": "Yazım Kuralları",
        "tarihlerin yazılışı": "Yazım Kuralları",
        "kısaltmaların yazılışı": "Yazım Kuralları",
        "büyük harflerin kullanımı": "Yazım Kuralları",
        "büyük harflerin yazılışı": "Yazım Kuralları",
        "birleşik sözcüklerin yazılışı": "Yazım Kuralları",
        "birleşik kelimelerin yazılışı": "Yazım Kuralları",
        "düzeltme işaretinin (^) kullanımı": "Yazım Kuralları",
        "düzeltme işaretinin kullanımı": "Yazım Kuralları",

        "noktalama işaretleri": "Noktalama İşaretleri",
        "noktalama": "Noktalama İşaretleri",
        "virgül": "Noktalama İşaretleri",
        "noktalı virgül": "Noktalama İşaretleri",
        "iki nokta": "Noktalama İşaretleri",
        "üç nokta": "Noktalama İşaretleri",
        "kesme işareti": "Noktalama İşaretleri",
        "nokta": "Noktalama İşaretleri",
        "nokta, virgül ve noktalı virgül": "Noktalama İşaretleri",
        "nokta ve virgül": "Noktalama İşaretleri",
        "virgül ve noktalı virgül": "Noktalama İşaretleri",
        "iki nokta ve üç nokta": "Noktalama İşaretleri",
        "soru ve ünlem işareti": "Noktalama İşaretleri",
        "kısa çizgi ve uzun çizgi": "Noktalama İşaretleri",
        "kısa çizgi": "Noktalama İşaretleri",
        "uzun çizgi": "Noktalama İşaretleri",
        "tırnak ve tek tırnak işareti": "Noktalama İşaretleri",
        "tırnak işareti": "Noktalama İşaretleri",
        "tek tırnak işareti": "Noktalama İşaretleri",
        "yay ayraç, köşeli ayraç ve kesme işareti": "Noktalama İşaretleri",
        "yay ayraç ve kesme işareti": "Noktalama İşaretleri",
        "yay ayraç": "Noktalama İşaretleri",
        "doğru yazalım doğru kullanalım sözlüğü": "Yazım Kuralları",
        "yazım kuralları sözlüğü": "Yazım Kuralları",
        "yazım kuralları uygulama ve etkinlikler": "Yazım Kuralları",
        "noktalama işaretleri uygulama ve etkinlikler": "Noktalama İşaretleri",

        "sözcük türleri": "Sözcük Türleri",
        "sözcük türü": "Sözcük Türleri",
        "isim": "Sözcük Türleri",
        "sıfat": "Sözcük Türleri",
        "zamir": "Sözcük Türleri",
        "zarf": "Sözcük Türleri",
        "edat": "Sözcük Türleri",
        "bağlaç": "Sözcük Türleri",
        "ünlem": "Sözcük Türleri",
        "fiil": "Sözcük Türleri",
        "fiiller": "Sözcük Türleri",
        "fiiller ve fiilimsiler": "Sözcük Türleri",
        "eylemler": "Sözcük Türleri",
        "fiilimsiler": "Sözcük Türleri",
        "fiilimsi": "Sözcük Türleri",
        "fiilde çatı": "Sözcük Türleri",
        "ek eylem": "Sözcük Türleri",

        "cümlenin ögeleri - cümle türleri": "Cümlenin Ögeleri - Cümle Türleri",
        "cümlenin ögeleri": "Cümlenin Ögeleri - Cümle Türleri",
        "cümle türleri": "Cümlenin Ögeleri - Cümle Türleri",
        "cümle ögeleri": "Cümlenin Ögeleri - Cümle Türleri",
        "ögeler": "Cümlenin Ögeleri - Cümle Türleri",
        "özne": "Cümlenin Ögeleri - Cümle Türleri",
        "yüklem": "Cümlenin Ögeleri - Cümle Türleri",
        "nesne": "Cümlenin Ögeleri - Cümle Türleri",
        "tümleç": "Cümlenin Ögeleri - Cümle Türleri",
        "cümle türü": "Cümlenin Ögeleri - Cümle Türleri",
        "birleşik cümle": "Cümlenin Ögeleri - Cümle Türleri",
        "basit cümle": "Cümlenin Ögeleri - Cümle Türleri",
        "sıralı cümle": "Cümlenin Ögeleri - Cümle Türleri",
        "anlatım bozuklukları": "Cümlenin Ögeleri - Cümle Türleri",
        "anlatım bozukluğu": "Cümlenin Ögeleri - Cümle Türleri",
        "anlam belirsizliği": "Cümlenin Ögeleri - Cümle Türleri",
        "anlamdan kaynaklanan anlatım bozuklukları": "Cümlenin Ögeleri - Cümle Türleri",
        "dil bilgisinden kaynaklanan anlatım bozuklukları": "Cümlenin Ögeleri - Cümle Türleri",
        "gereksiz sözcük kullanımı": "Cümlenin Ögeleri - Cümle Türleri",
        "gereksiz yardımcı eylem kullanımı": "Cümlenin Ögeleri - Cümle Türleri",
        "yanlış anlamda sözcük kullanımı": "Cümlenin Ögeleri - Cümle Türleri",
        "sözcüğün yapısından kaynaklanan anlatım bozuklukları": "Cümlenin Ögeleri - Cümle Türleri",
        "sözcüğün yanlış yerde kullanımı": "Cümlenin Ögeleri - Cümle Türleri",
        "anlamca çelişen sözcüklerin bir arada kullanımı": "Cümlenin Ögeleri - Cümle Türleri",
        "deyim ve atasözü yanlışlıkları": "Cümlenin Ögeleri - Cümle Türleri",
        "mantık hataları": "Cümlenin Ögeleri - Cümle Türleri",
        "karşılaştırma hataları": "Cümlenin Ögeleri - Cümle Türleri",
        "noktalama eksikliği": "Cümlenin Ögeleri - Cümle Türleri",
        "yüklem yanlışlığı": "Cümlenin Ögeleri - Cümle Türleri",
        "özne yanlışlığı": "Cümlenin Ögeleri - Cümle Türleri",
        "özne - yüklem uyuşmazlığı": "Cümlenin Ögeleri - Cümle Türleri",
        "nesne eksikliği": "Cümlenin Ögeleri - Cümle Türleri",
        "dolaylı tümleç eksikliği": "Cümlenin Ögeleri - Cümle Türleri",
        "zarf tümleci eksikliği": "Cümlenin Ögeleri - Cümle Türleri",
        "tamlama yanlışları": "Cümlenin Ögeleri - Cümle Türleri",
        "çatı uyuşmazlığı": "Cümlenin Ögeleri - Cümle Türleri",

        "sözel mantık": "Sözel Mantık",
        "mantık": "Sözel Mantık",
        "akıl yürütme": "Sözel Mantık",
        "tablo ve sıralama": "Sözel Mantık",
        "eşleştirme": "Sözel Mantık",
        "matris senaryoları": "Sözel Mantık",

        // Zihin Ağı Düğüm Kimlikleri
        "trk_sozcuk_anlam": "Sözcükte Anlam",
        "trk_cumle_anlam": "Cümlede Anlam",
        "trk_paragraf": "Paragraf",
        "trk_ses": "Ses Bilgisi",
        "trk_yazim": "Yazım Kuralları",
        "trk_noktalama": "Noktalama İşaretleri",
        "trk_sozcuk_yapi": "Biçim Bilgisi",
        "trk_sozcuk_tur": "Sözcük Türleri",
        "trk_fiil": "Sözcük Türleri",
        "trk_oge": "Cümlenin Ögeleri - Cümle Türleri",
        "trk_cumle_tur": "Cümlenin Ögeleri - Cümle Türleri",
        "trk_anlatim": "Cümlenin Ögeleri - Cümle Türleri",
        "trk_sozel_mantik": "Sözel Mantık",
    },
    math: {
        "tek - çift sayılar": "Tek ve Çift Sayılar",
        "tek çift sayılar": "Tek ve Çift Sayılar",
        "tek ve çift sayılar": "Tek ve Çift Sayılar",
        "tek çift": "Tek ve Çift Sayılar",

        "doğal sayılar": "Temel Kavramlar",
        "tam sayılar": "Temel Kavramlar",
        "sayı doğrusu": "Temel Kavramlar",
        "asal sayı": "Asal Sayılar",
        "asal sayılar": "Asal Sayılar",
        "ardışık sayılar": "Ardışık Sayılar",
        "faktöriyel": "Faktöriyel",

        "basamak": "Basamak Analizi",
        "basamak analizi": "Basamak Analizi",
        "sayı basamakları": "Basamak Analizi",
        "onlar basamağı": "Basamak Analizi",

        "bölme": "Bölme - Bölünebilme",
        "bölünebilme": "Bölme - Bölünebilme",
        "bölme - bölünebilme": "Bölme - Bölünebilme",
        "bölme ve bölünebilme": "Bölme - Bölünebilme",

        "ebob": "Ebob - Ekok",
        "ekok": "Ebob - Ekok",
        "ebob - ekok": "Ebob - Ekok",
        "ebob ve ekok": "Ebob - Ekok",

        "rasyonel": "Rasyonel Sayılar",
        "rasyonel sayılar": "Rasyonel Sayılar",
        "ondalık": "Ondalıklı Sayılar",
        "ondalıklı sayılar": "Ondalıklı Sayılar",

        "basit eşitsizlik": "Pozitif - Negatif Sayılar ve Basit Eşitsizlikler",
        "basit eşitsizlikler": "Pozitif - Negatif Sayılar ve Basit Eşitsizlikler",
        "eşitsizlik": "Pozitif - Negatif Sayılar ve Basit Eşitsizlikler",
        "mutlak değer": "Mutlak Değer",
        "üslü sayılar": "Üslü Sayılar",
        "üslü ifade": "Üslü Sayılar",
        "köklü sayılar": "Köklü Sayılar",
        "köklü ifade": "Köklü Sayılar",
        "çarpanlara ayırma": "Çarpanlara Ayırma",
        "denklem çözme": "Denklem Çözme",

        "oran": "Oran Orantı",
        "orantı": "Oran Orantı",
        "oran orantı": "Oran Orantı",
        "oran - orantı": "Oran Orantı",

        "sayı problemleri": "Sayı - Kesir Problemleri",
        "kesir problemleri": "Sayı - Kesir Problemleri",
        "sayı - kesir problemleri": "Sayı - Kesir Problemleri",
        "problemler": "Sayı - Kesir Problemleri",
        "yaş problemleri": "Yaş Problemleri",
        "yüzde problemleri": "Yüzde Problemleri",
        "kar - zarar problemleri": "Kar - Zarar Problemleri",
        "kar zarar": "Kar - Zarar Problemleri",
        "karışım problemi": "Karışım Problemi",
        "karışım problemleri": "Karışım Problemi",
        "periyodik problemler": "Periyodik Problemler",
        "hareket problemleri": "Hareket Problemleri",
        "hız problemleri": "Hareket Problemleri",
        "işçi problemleri": "İşçi Problemleri",
        "işçi havuz": "İşçi Problemleri",
        "tablo grafik problemi": "Tablo Grafik Problemi",
        "grafik problemleri": "Tablo Grafik Problemi",

        "küme": "Kümeler",
        "kümeler": "Kümeler",
        "venn": "Kümeler",
        "fonksiyon": "Fonksiyonlar",
        "fonksiyonlar": "Fonksiyonlar",
        "işlem": "İşlem",
        "özel sayılar": "Özel Sayılar",
        "özel sayı": "Özel Sayılar",

        "permütasyon": "Permütasyon-Kombinasyon-Olasılık",
        "kombinasyon": "Permütasyon-Kombinasyon-Olasılık",
        "olasılık": "Permütasyon-Kombinasyon-Olasılık",
        "permütasyon - kombinasyon - olasılık": "Permütasyon-Kombinasyon-Olasılık",

        "sayısal mantık": "Sayısal Mantık",
        "mantık": "Sayısal Mantık",

        "üçgenler": "Doğruda ve Üçgende Açılar",
        "üçgen": "Dik, İkizkenar ve Eşkenar Üçgen",
        "çokgenler": "Çokgenler",
        "dörtgenler": "Dörtgenler",
        "çember - daire": "Çember - Daire",
        "çember": "Çember - Daire",
        "daire": "Çember - Daire",
        "katı cisim": "Katı Cisimler",
        "katı cisimler": "Katı Cisimler",
        "analitik geometri": "Analitik Geometri",
        "geometri": "Doğruda ve Üçgende Açılar",

        // Zihin Ağı Düğüm Kimlikleri
        "mat_temel": "Temel Kavramlar",
        "mat_basamak": "Basamak Analizi",
        "mat_bolme": "Bölme - Bölünebilme",
        "mat_ebob": "Ebob - Ekok",
        "mat_rasyonel": "Rasyonel Sayılar",
        "mat_ondalik": "Ondalıklı Sayılar",
        "mat_esitsizlik": "Pozitif - Negatif Sayılar ve Basit Eşitsizlikler",
        "mat_mutlak": "Mutlak Değer",
        "mat_uslu": "Üslü Sayılar",
        "mat_koklu": "Köklü Sayılar",
        "mat_carpanlar": "Çarpanlara Ayırma",
        "mat_denklem": "Denklem Çözme",
        "mat_oran": "Oran Orantı",
        "mat_kume": "Kümeler",
        "mat_sayi_prob": "Sayı - Kesir Problemleri",
        "mat_yas": "Yaş Problemleri",
        "mat_yuzde": "Yüzde Problemleri",
        "mat_kar_zarar": "Kar - Zarar Problemleri",
        "mat_karisim": "Karışım Problemi",
        "mat_hareket": "Hareket Problemleri",
        "mat_isci": "İşçi Problemleri",
        "mat_fonksiyon": "Fonksiyonlar",
        "mat_periyodik": "Periyodik Problemler",
        "mat_islem": "İşlem",
        "mat_ozel_sayilar": "Özel Sayılar",
        "mat_permutasyon": "Permütasyon-Kombinasyon-Olasılık",
        "mat_mantik": "Sayısal Mantık",
        "mat_grafik": "Tablo Grafik Problemi",
        "mat_geometri": "Doğruda ve Üçgende Açılar",
    },
    history: {
        "u1": "İlk ve Orta Çağlarda Türk Dünyası",
        "history_u1": "İlk ve Orta Çağlarda Türk Dünyası",
        "u2": "Türklerin İslamiyet'i Kabulü ve İlk Türk-İslam Devletleri",
        "history_u2": "Türklerin İslamiyet'i Kabulü ve İlk Türk-İslam Devletleri",
        "u3": "Yerleşme ve Devletleşme Sürecinde Selçuklu Türkiyesi",
        "history_u3": "Yerleşme ve Devletleşme Sürecinde Selçuklu Türkiyesi",
        "u4": "Osmanlı Devleti Kültür ve Medeniyeti",
        "history_u4": "Osmanlı Devleti Kültür ve Medeniyeti",
        "u5": "Beylikten Devlete Osmanlı Devleti",
        "history_u5": "Beylikten Devlete Osmanlı Devleti",
        "u6": "Dünya Gücü Osmanlı",
        "history_u6": "Dünya Gücü Osmanlı",
        "u7": "Değişen Dünya Dengeleri Karşısında Osmanlı Devleti (XVII. Yüzyıl)",
        "history_u7": "Değişen Dünya Dengeleri Karşısında Osmanlı Devleti (XVII. Yüzyıl)",
        "u8": "Değişen Dünya Dengeleri Karşısında Osmanlı Siyaseti (XVIII. Yüzyıl)",
        "history_u8": "Değişen Dünya Dengeleri Karşısında Osmanlı Siyaseti (XVIII. Yüzyıl)",
        "u9": "En Uzun Yüzyıl (XIX. Yüzyılda Osmanlı Devleti)",
        "history_u9": "En Uzun Yüzyıl (XIX. Yüzyılda Osmanlı Devleti)",
        "u10": "XIX. Yüzyıl Osmanlı Devleti Islahatları",
        "history_u10": "XIX. Yüzyıl Osmanlı Devleti Islahatları",
        "u11": "XX. Yüzyıl Başlarında Osmanlı Devleti ve Dünya",
        "history_u11": "XX. Yüzyıl Başlarında Osmanlı Devleti ve Dünya",
        "u12": "Millî Mücadele Hazırlık Dönemi",
        "history_u12": "Millî Mücadele Hazırlık Dönemi",
        "u13": "I. TBMM'nin Açılması, İç Ayaklanmalar ve Sevr Barış Antlaşması",
        "history_u13": "I. TBMM'nin Açılması, İç Ayaklanmalar ve Sevr Barış Antlaşması",
        "u14": "Millî Mücadele Muharebeler Dönemi",
        "history_u14": "Millî Mücadele Muharebeler Dönemi",
        "u15": "Mustafa Kemal Atatürk'ün Hayatı",
        "history_u15": "Mustafa Kemal Atatürk'ün Hayatı",
        "u16": "Atatürk Dönemi İç Politika Gelişmeleri",
        "history_u16": "Atatürk Dönemi İç Politika Gelişmeleri",
        "u17": "Atatürk İlkeleri",
        "history_u17": "Atatürk İlkeleri",
        "u18": "Atatürk İnkılapları",
        "history_u18": "Atatürk İnkılapları",
        "u19": "Cumhuriyet Dönemi Kültür ve Medeniyeti",
        "history_u19": "Cumhuriyet Dönemi Kültür ve Medeniyeti",
        "u20": "Atatürk Dönemi Dış Politikası",
        "history_u20": "Atatürk Dönemi Dış Politikası",
        "u21": "Çağdaş Türk ve Dünya Tarihi",
        "history_u21": "Çağdaş Türk ve Dünya Tarihi",
    }
};

/**
 * Tarih: chronology.ts alt başlığı → ünite label'ı (label'lar blueprint topic'leriyle birebir aynı).
 * Alt başlıklar (ör. "Hazar Devleti", "Güney Cephesi") blueprint'te geçmediği için bu harita olmadan
 * eşleşme bulunamıyor veya kelime skoruyla yanlış üniteye (çoğunlukla u1/u4) düşüyordu.
 */
const HISTORY_SUBTOPIC_TO_UNIT: { key: string; label: string }[] = HISTORY_CHRONOLOGY.flatMap(unit =>
    unit.subTopics.map(sub => ({ key: trLower(sub), label: unit.label }))
);
const HISTORY_SUBTOPIC_EXACT = new Map(HISTORY_SUBTOPIC_TO_UNIT.map(e => [e.key, e.label]));

/** Kelime skoru adayları: tarihte ünite label'ı + alt başlıkları, diğer derslerde blueprint topic'leri */
const getFuzzyCandidates = (subject: SubjectType, blueprint: TopicWeight[]): { text: string; topic: string }[] => {
    if (subject === 'history') {
        return HISTORY_CHRONOLOGY.flatMap(unit => [
            { text: unit.label, topic: unit.label },
            ...unit.subTopics.map(sub => ({ text: sub, topic: unit.label }))
        ]);
    }
    return blueprint.map(bp => ({ text: bp.topic, topic: bp.topic }));
};

// Saf fonksiyon (statik veri) → sonuçlar önbelleğe alınır; havuz filtrelerinde soru × konu kadar çağrılıyor.
const NORMALIZE_CACHE = new Map<string, { topic: string; matched: boolean }>();
const NORMALIZE_CACHE_LIMIT = 5000;
const WARNED_UNMATCHED = new Set<string>();

/**
 * AI'ın ürettiği serbest topic metnini, blueprint'teki en yakın konu ismine eşler.
 * 
 * Eşleştirme sırası:
 * 0. Alias map (kesin eşleştirme — AI alt-konu → blueprint ana konu)
 * 1. Tam eşleşme (exact) — tarihte chronology alt başlığı da dahil
 * 2. includes eşleşmesi (blueprint rawTopic'i içeriyor veya tam tersi) — tarihte alt başlıklar da dahil
 * 3. Keyword tabanlı alias arama (alt-string match)
 * 4. Kelime kesişimi skoru (fuzzy — en çok ortak kelime)
 * 5. Hiçbiri eşleşmezse → ilk blueprint konusu (uyarı her benzersiz isim için bir kez basılır)
 */
export function normalizeTopicName(rawTopic: string, subject: SubjectType, suppressWarning?: boolean): string {
    if (!rawTopic) return 'Genel';
    
    const blueprint = EXAM_BLUEPRINTS[subject];
    if (!blueprint || blueprint.length === 0) return rawTopic;

    const cacheKey = `${subject}|${rawTopic}`;
    let result = NORMALIZE_CACHE.get(cacheKey);
    if (!result) {
        result = computeNormalizedTopic(rawTopic, subject, blueprint);
        if (NORMALIZE_CACHE.size >= NORMALIZE_CACHE_LIMIT) NORMALIZE_CACHE.clear();
        NORMALIZE_CACHE.set(cacheKey, result);
    }

    if (!result.matched && !suppressWarning && !WARNED_UNMATCHED.has(cacheKey)) {
        WARNED_UNMATCHED.add(cacheKey);
        console.warn(`%c[normalizeTopicName] ⚠️ Eşleşme bulunamadı, ilk blueprint'e atandı: "${rawTopic}" → "${result.topic}" (${subject})`, 'color:#f59e0b');
    }
    return result.topic;
}

/** normalizeTopicName ile aynı eşleştirme; ek olarak gerçekten eşleşip eşleşmediğini (ilk üniteye düşme değil) söyler. */
export function matchTopicName(rawTopic: string, subject: SubjectType): { topic: string; matched: boolean } {
    if (!rawTopic || !rawTopic.trim()) return { topic: 'Genel', matched: false };
    const blueprint = EXAM_BLUEPRINTS[subject];
    if (!blueprint || blueprint.length === 0) return { topic: rawTopic, matched: true };
    const cacheKey = `${subject}|${rawTopic}`;
    let result = NORMALIZE_CACHE.get(cacheKey);
    if (!result) {
        result = computeNormalizedTopic(rawTopic, subject, blueprint);
        if (NORMALIZE_CACHE.size >= NORMALIZE_CACHE_LIMIT) NORMALIZE_CACHE.clear();
        NORMALIZE_CACHE.set(cacheKey, result);
    }
    return result;
}

function computeNormalizedTopic(rawTopic: string, subject: SubjectType, blueprint: TopicWeight[]): { topic: string; matched: boolean } {
    const hit = (topic: string) => ({ topic, matched: true });
    const raw = trLower(rawTopic);

    // 0. Alias map — kesin eşleştirme (tam key match)
    const subjectAliases = TOPIC_ALIASES[subject] || {};
    if (subjectAliases[raw]) {
        return hit(subjectAliases[raw]);
    }

    // 1. Tam eşleşme
    for (const bp of blueprint) {
        if (bp.topics) {
            for (const t of bp.topics) {
                if (trLower(t) === raw) return hit(t);
            }
        }
        if (trLower(bp.topic) === raw) {
            return hit(bp.topic);
        }
    }

    // 1b. Tarih: chronology alt başlığı → ünite
    if (subject === 'history') {
        const unit = HISTORY_SUBTOPIC_EXACT.get(raw);
        if (unit) return hit(unit);
    }

    // 2. includes eşleşmesi (çift yönlü)
    for (const bp of blueprint) {
        if (bp.topics) {
            for (const t of bp.topics) {
                const tLow = trLower(t);
                if (tLow.includes(raw) || raw.includes(tLow)) return hit(t);
            }
        }
        const bpLow = trLower(bp.topic);
        if (bpLow.includes(raw) || raw.includes(bpLow)) {
            return hit(bp.topic);
        }
    }

    // 2b. Tarih: alt başlık includes (çok kısa metinler yanlış eşleşmesin diye ≥4 karakter)
    if (subject === 'history' && raw.length >= 4) {
        for (const e of HISTORY_SUBTOPIC_TO_UNIT) {
            if (e.key.includes(raw) || raw.includes(e.key)) return hit(e.label);
        }
    }

    // 3. Keyword alias arama — rawTopic alias key'lerinden birini İÇERİYOR mu?
    // En UZUN (en belirgin) eşleşme kazanır: "Ulaşım (Demiryolu)" ilk bulunan "demir" yüzünden Madenler'e gidiyordu.
    let bestAlias = '';
    let bestAliasTopic = '';
    for (const [aliasKey, blueprintTopic] of Object.entries(subjectAliases)) {
        if ((raw.includes(aliasKey) || aliasKey.includes(raw)) && aliasKey.length > bestAlias.length) {
            bestAlias = aliasKey;
            bestAliasTopic = blueprintTopic;
        }
    }
    if (bestAliasTopic) return hit(bestAliasTopic);

    // 4. Kelime kesişimi skoru
    // Vatandaşlıkta "türk / tarih(i)" gibi genel kelimeler tek başına eşleşme sebebi olamaz: "Türk-İslam Tarihi" gibi
    // tarih konuları "Türk Anayasa Tarihi"ne düşüyordu (2026-09-29, vatandaşlık denemesine tarih sorusu geldi).
    const GENERIC_WORDS = subject === 'citizenship' ? new Set(['türk', 'türkiye', 'tarih', 'tarihi', 'dönemi', 'devleti']) : null;
    const rawWords = extractWords(rawTopic).filter(w => !GENERIC_WORDS?.has(w));
    let bestMatch = '';
    let bestScore = 0;

    for (const cand of getFuzzyCandidates(subject, blueprint)) {
        const candWords = extractWords(cand.text);
        const intersection = rawWords.filter(w => candWords.some(bw => bw.includes(w) || w.includes(bw)));
        const score = intersection.length / Math.max(rawWords.length, candWords.length, 1);
        if (score > bestScore) {
            bestScore = score;
            bestMatch = cand.topic;
        }
    }

    if (bestScore > 0 && bestMatch) {
        return hit(bestMatch);
    }

    // 5. Vatandaşlık için: Kültür, sanat, uluslararası veya güncel anahtar kelimeleri varsa asla ilk blueprint konusuna düşürme, Güncel Bilgiler yap!
    if (subject === 'citizenship' && isGuncelOrGenelKultur({ topic: rawTopic })) {
        return hit("Güncel Bilgiler");
    }

    // Hiçbir eşleşme bulunamazsa ilk blueprint konusunu döndür
    return { topic: blueprint[0].topic, matched: false };
}

/**
 * KPSS Vatandaşlık dersinde Güncel Bilgiler, Genel Kültür, Uluslararası Kuruluşlar ve Kültür/Sanat
 * sorularını kesin olarak tespit eder.
 * Rastgele tohum (seed) veya havuzdan soru çekilirken ilk 9 saf vatandaşlık sorusuna sızmasını engeller.
 */
export function isGuncelOrGenelKultur(q: { topic?: string; source?: string; sourceTag?: string; [key: string]: any }): boolean {
    if (!q) return false;
    if (q.source === 'guncel_exams' || q.sourceTag === 'guncel_depo' || q.source === 'citizenshipGuncel2026') return true;
    const rawTopic = (q.topic || '').toLowerCase();
    if (rawTopic.includes('güncel') || rawTopic.includes('guncel')) return true;
    if (rawTopic.includes('genel kültür') || rawTopic.includes('genel kultur')) return true;
    if (rawTopic.includes('uluslararası') || rawTopic.includes('uluslararasi')) return true;
    if (rawTopic.includes('sanat') || rawTopic.includes('edebiyat') || rawTopic.includes('unesco') || rawTopic.includes('nobel') || rawTopic.includes('türksoy') || rawTopic.includes('türksav')) return true;
    return false;
}

/**
 * 🔒 KESİN VE SIKI GÜNCEL BİLGİLER FİLTRESİ
 * SADECE BİZİM EKLEDİĞİMİZ GÜNCEL SORULARI KABUL EDER.
 * Geçmiş yıllara ait eski ÖSYM çıkmış güncel sorularını (2012-2024 arası bayatlamış soruları) KESİNLİKLE REDDEDER!
 */
export function isStrictCuratedGuncel(q: { topic?: string; source?: string; sourceTag?: string; year?: string; isPredicted?: boolean; [key: string]: any }): boolean {
    if (!q) return false;
    // 1. Çıkmış sınav veya eski yayınevi sorularını KESİNLİKLE ele!
    if (q.source === 'OSYM_Cikmis' || q.source === 'Yayinevi' || q.sourceType === 'past_exam' || q.questionType === 'past-exam') return false;
    const yr = String(q.year || '');
    if (yr.includes('KPSS') && !yr.includes('Tahmin') && !yr.includes('2026') && !yr.includes('2025')) return false;
    if (['2012', '2013', '2014', '2015', '2016', '2017', '2018', '2019', '2020', '2021', '2022', '2023', '2024'].some(y => yr.includes(y))) return false;

    // 2. Yalnızca bizim küratörlüğünü yaptığımız güvenilir yeni kaynaklar
    if (q.source === 'guncel_exams' || q.sourceTag === 'guncel_depo' || q.source === 'citizenshipGuncel2026' || q.source === 'Ozel_Deneme') return true;
    if (yr.includes('2025') || yr.includes('2026')) return true;
    if (q.isCuratedGuncel === true) return true;
    return false;
}


/**
 * Automatically and deterministically detects the mathematical topic from question content,
 * mathematical notation, keywords, and formula patterns.
 */
export function detectMathTopicFromContent(questionText: string, options?: string[]): string | null {
    if (!questionText) return null;
    const fullText = trLower(questionText + ' ' + (options ? options.join(' ') : ''));

    // 1. Köklü Sayılar (√, \sqrt, karekök, küpkök)
    if (/[√]|\\sqrt|karekök|küpkök|dereceden kök/i.test(fullText)) {
        return "Köklü Sayılar";
    }

    // 2. Faktöriyel (n!, 5!, !, faktöriyel)
    if (/\b\d+\s*!|\b[nka-z]\s*!|faktöriyel/i.test(fullText)) {
        return "Faktöriyel";
    }

    // 3. Fonksiyonlar (f(x), g(x), (f o g), birebir, örten, ters fonksiyon)
    if (/\bf\s*\(\s*[0-9a-z+\-·/]+\s*\)|\bg\s*\(\s*[0-9a-z+\-·/]+\s*\)|fonksiyon|(?:f\s*o\s*g)|(?:g\s*o\s*f)/i.test(fullText)) {
        return "Fonksiyonlar";
    }

    // 4. Kümeler (küme, kesişim, birleşim, alt küme, evrensel küme, s(A), s(B), A ∩ B, A ∪ B)
    if (/küme|kesişim|birleşim|alt küme|evrensel küme|s\([a-z]\)|∩|∪|\\cap|\\cup/i.test(fullText)) {
        return "Kümeler";
    }

    // 5. İşlem (özel işlem sembolü: x * y, a ⊕ b, a △ b, a ★ b, işlemi tanımlanıyor)
    if (/işlemi tanımlanıyor|işlemine göre|biçiminde tanımlanıyor|[a-z0-9]\s*[⊕⊗★△▲▼▽]\s*[a-z0-9]/i.test(fullText)) {
        return "İşlem";
    }

    // 6. Permütasyon, Kombinasyon, Olasılık
    if (/permütasyon|kombinasyon|olasılık|olasılığı kaçtır|torbada|zar |madeni para|bilye|seçilen.*rastgele/i.test(fullText)) {
        return "Permütasyon-Kombinasyon-Olasılık";
    }

    // 7. Modüler Aritmetik / Periyodik Problemler (mod, periyot, nöbet, gün sonra hangi gün)
    if (/\bmod\b|kalanlı bölme|nöbet|periyodik|kaç gün sonra|kaçıncı gün/i.test(fullText)) {
        return "Periyodik Problemler";
    }

    // 8. Yaş Problemleri
    if (/yaşında|yaşları|yaşına|yaşındadır|bugünkü yaşı|\byaşı\b|\byaş\b/i.test(fullText)) {
        return "Yaş Problemleri";
    }

    // 9. Hareket Problemleri (hız, km/sa, yolculuk, zıt yönde, aynı yönde)
    if (/km\/sa|hızı saatte|saatteki hızı|aynı anda birbirlerine doğru|aynı yönde hareket|ortalama hız/i.test(fullText)) {
        return "Hareket Problemleri";
    }

    // 10. İşçi Problemleri
    if (/işçi|usta.*çırak|günde bitiriyor|birlikte.*işi|işin tamamını.*günde/i.test(fullText)) {
        return "İşçi Problemleri";
    }

    // 11. Karışım Problemleri
    if (/karışım|tuz oranı|şeker oranı|alkol oranı|saf su|karıştırılıyor|yeni karışımın/i.test(fullText)) {
        return "Karışım Problemi";
    }

    // 12. Kar - Zarar Problemleri
    if (/kar oranı|zarar oranı|maliyet|satış fiyatı|alış fiyatı|iskonto|indirimli satış|etiket fiyatı/i.test(fullText)) {
        return "Kar - Zarar Problemleri";
    }

    // 13. Yüzde Problemleri
    if (/%|yüzde\s*\d+|\d+\s*yüzdesi|yüzdesi kaçtır/i.test(fullText)) {
        return "Yüzde Problemleri";
    }

    // 14. Geometri - Doğruda ve Üçgende Açılar, Dörtgenler, Çember, Katı Cisimler
    if (/çember|daire|yarıçap|teğet|kiriş|çapı/i.test(fullText)) {
        return "Çember - Daire";
    }
    if (/silindir|koni|küre|prizma|küp|hacmi kaçtır|katı cisim/i.test(fullText)) {
        return "Katı Cisimler";
    }
    if (/paralelkenar|eşkenar dörtgen|dikdörtgen|kare|yamuk|deltoid|dörtgen/i.test(fullText)) {
        return "Dörtgenler";
    }
    if (/düzgün beşgen|düzgün altıgen|düzgün sekizgen|iç açısı|dış açısı|çokgen/i.test(fullText)) {
        return "Çokgenler";
    }
    if (/açıortay|kenarortay|ağırlık merkezi|iç teğet çemberin merkezi/i.test(fullText)) {
        return "Açıortay ve Kenarortay";
    }
    if (/benzerlik oranı|üçgende alan|alanı.*kaç cm²|alanı.*kaç birimkare/i.test(fullText)) {
        return "Üçgende Alan ve Benzerlik";
    }
    if (/dik üçgen|ikizkenar üçgen|eşkenar üçgen|pisagor|öklid|hipotenüs/i.test(fullText)) {
        return "Dik, İkizkenar ve Eşkenar Üçgen";
    }
    if (/üçgen|doğruda açı|tümler|bütünler|yöndeş|iç ters|m\(.*\)|\b\d+°|açısının ölçüsü|\[ab\]|\[bc\]|\[ac\]|\[cd\]/i.test(fullText)) {
        return "Doğruda ve Üçgende Açılar";
    }
    if (/analitik düzlem|eğimi|doğrunun denklemi|orijin|koordinat sistemi|a\(x,\s*y\)/i.test(fullText)) {
        return "Analitik Geometri";
    }

    // 15. Çarpanlara Ayırma (özdeşlik, sadeleştirilmiş hali, x² - y², en sade biçimi)
    if (/en sade (?:biçimi|hali)|sadeleştirilmiş (?:biçimi|hali)|çarpanlarından biridir|özdeşlik/i.test(fullText)) {
        return "Çarpanlara Ayırma";
    }

    // 16. Mutlak Değer (|x|, |a-b|, mutlak değer)
    if (/\|[a-z0-9+\-·/ ]+\||mutlak değer/i.test(fullText)) {
        return "Mutlak Değer";
    }

    // 17. Üslü Sayılar (x², 2ⁿ, 3ˣ, üslü ifade, kuvveti)
    if (/[⁰¹²³⁴⁵⁶⁷⁸⁹]\s*[+\-*=]|[\d][\^][\d+a-z]|üslü sayı|kuvveti kaçtır/i.test(fullText)) {
        return "Üslü Sayılar";
    }

    // 18. Pozitif - Negatif Sayılar ve Basit Eşitsizlikler (eşitsizlik, <, >, ≤, ≥, aralığı)
    if (/eşitsizliğ|küçüktür|büyüktür|[a-z0-9]\s*[<≤>≥]\s*[a-z0-9]/i.test(fullText)) {
        return "Pozitif - Negatif Sayılar ve Basit Eşitsizlikler";
    }

    // 19. Ebob - Ekok
    if (/ebob|ekok|en büyük ortak bölen|en küçük ortak kat/i.test(fullText)) {
        return "Ebob - Ekok";
    }

    // 20. Bölme - Bölünebilme
    if (/bölümünden kalan|tam bölünür|ile bölünebilme|tam bölünebilen/i.test(fullText)) {
        return "Bölme - Bölünebilme";
    }

    // 21. Basamak Analizi
    if (/üç basamaklı|iki basamaklı|dört basamaklı|basamaklı|rakamları toplamı|rakamları farklı|sayı basamakları/i.test(fullText)) {
        return "Basamak Analizi";
    }

    // 22. Tek ve Çift Sayılar
    if (/tek sayıdır|çift sayıdır|tektir|çifttir|hangisi daima tektir|hangisi daima çifttir/i.test(fullText)) {
        return "Tek ve Çift Sayılar";
    }

    // 23. Asal Sayılar
    if (/asal sayı|aralarında asal|asal bölen|asal çarpan/i.test(fullText)) {
        return "Asal Sayılar";
    }

    // 24. Ardışık Sayılar
    if (/ardışık/i.test(fullText)) {
        return "Ardışık Sayılar";
    }

    // 25. Oran Orantı
    if (/oranı kaçtır|orantılıdır|doğru orantılı|ters orantılı/i.test(fullText)) {
        return "Oran Orantı";
    }

    // 26. Rasyonel & Ondalıklı Sayılar
    if (/ondalık|virgüllü/i.test(fullText)) {
        return "Ondalıklı Sayılar";
    }
    if (/\b\d+\s*\/\s*\d+\b|rasyonel/i.test(fullText)) {
        return "Rasyonel Sayılar";
    }

    // 27. Özel Sayılar
    if (/sayısı denir|sayısı olarak adlandırılır|özel sayı/i.test(fullText)) {
        return "Özel Sayılar";
    }

    return null;
}

/**
 * Resolves a question's topic with 100% deterministic accuracy:
 * 1. Content-based math classifier (detectMathTopicFromContent)
 * 2. Normalization of AI-provided topic (normalizeTopicName)
 * 3. Exact blueprint seed fallback (fallbackTopic)
 */
export function resolveQuestionTopic(
    q: { question: string; options?: string[]; topic?: string },
    fallbackTopic: string = 'Genel',
    subject: SubjectType = 'geography'
): string {
    // 0. Tohumlanan / küratörlü ünite önceliklidir: sorunun konusu (yoksa yedek konu) bir üniteye GERÇEKTEN
    //    eşleşiyorsa o kullanılır. Eskiden matematikte içerikten tahmin her şeyin önüne geçiyordu: "Tablo Grafik
    //    Problemi" sorusu "Çember - Daire"ye, "Dörtgenler" "Köklü Sayılar"a yazılıyordu (karne ↔ Zihin Ağı tutarsız).
    for (const candidate of [q.topic, fallbackTopic]) {
        if (!candidate || !candidate.trim() || candidate === 'Genel') continue;
        const m = matchTopicName(candidate, subject);
        if (m.matched && m.topic !== 'Genel') return m.topic;
    }

    // 1. Math content-based detector (konu yok ya da hiçbir üniteye eşleşmiyorsa)
    if (subject === 'math') {
        const detected = detectMathTopicFromContent(q.question, q.options);
        if (detected) return detected;
    }

    // 2. Normalize AI topic
    if (q.topic && q.topic.trim()) {
        const normalized = normalizeTopicName(q.topic, subject, true);
        if (normalized && normalized !== 'Genel') {
            return normalized;
        }
    }

    // 3. Fallback to blueprint seed
    if (fallbackTopic && fallbackTopic.trim()) {
        return normalizeTopicName(fallbackTopic, subject, true);
    }

    return 'Genel';
}

/**
 * Yapay zekâ çıktısında konu = TOHUM sorunun ünitesi (CLAUDE.md: "Konuyu AI'ya bırakma").
 * Tohum konusu yoksa/eşleşmiyorsa sorunun kendi konusu, o da yoksa içerik tespiti.
 */
export function resolveSeededTopic(
    seedTopic: string | undefined,
    q: { question: string; options?: string[]; topic?: string },
    subject: SubjectType
): string {
    const seed = seedTopic && seedTopic.trim() && seedTopic !== 'Genel' ? seedTopic : undefined;
    return resolveQuestionTopic({ ...q, topic: seed || q.topic }, q.topic || 'Genel', subject);
}

export const GEOMETRY_TOPIC_NAMES = new Set([
    "Doğruda ve Üçgende Açılar",
    "Dik, İkizkenar ve Eşkenar Üçgen",
    "Açıortay ve Kenarortay",
    "Üçgende Alan ve Benzerlik",
    "Çokgenler",
    "Dörtgenler",
    "Çember - Daire",
    "Katı Cisimler",
    "Analitik Geometri"
]);

export function isGeometryTopic(topicName?: string): boolean {
    if (!topicName || typeof topicName !== 'string') return false;
    const normalized = normalizeTopicName(topicName, 'math', true);
    if (GEOMETRY_TOPIC_NAMES.has(normalized)) return true;
    return /geometri|üçgen|dörtgen|çokgen|çember|daire|katı cisim|analitik|açıortay|kenarortay|pisagor|öklid/i.test(topicName);
}
